import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { useNavigate } from 'react-router-dom';
import {
  ButtonControl,
  FieldControl,
  FormControl,
  IconControl,
  PanelControl,
  SearchInput,
  SidePanel,
  SidePanelFormControl,
  TabPaneControl,
  TableControl,
  ToastNotifications,
} from 'spark-core-dx/components';
import { useCorePermissions, useGenericManager } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import ConvokeCommandBar from '../../components/convoke/ConvokeCommandBar';
import DialogControl from '../../components/convoke/ConvokeDialogControl';
import ConvokeDialogMessage from '../../components/convoke/ConvokeDialogMessage';
import AIPanel from '../../components/AIChat/AIPanel';
import PermissionSetEditor from '../../components/PermissionSetEditor';
import CardGridControl from '../resource/components/CardGridControl';
import ResourceStateControl from '../resource/components/ResourceStateControl';
import {
  getFirstMetadata,
  getFirstResultItem,
  getResultMessage,
  isSuccessfulResult,
} from './clientEntryContracts';
import {
  buildClientEntraSetupEmail,
  CLIENT_ENTRA_SETUP_SCRIPT_ITEM_NAME,
  CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
  downloadClientEntraSetupScript,
} from './clientEntraSetupScriptContracts';
import ClientEntraSetupGuidance from './ClientEntraSetupGuidance';
import { CLIENT_COMMAND_OPERATION_PERMISSIONS } from './clientCommandPermissions';
import {
  ACTIVE_AUTH_PROVIDER_FIELD,
  AUTH_MAPPING_CARD_METADATA,
  AUTH_PROVIDER_SELECTION_CARD_METADATA,
  AUTH_PROVIDER_SELECTION_OPTIONS,
  CLIENT_AZURE_MAPS_CONNECTION_FIELDS,
  CLIENT_ASSIGNED_USERS_GRID_METADATA,
  CLIENT_ENTRA_PROVIDER_SETUP_FIELDS,
  CLIENT_ENTRA_MAPPING_FIELDS,
  CLIENT_PERMISSION_GRID_METADATA,
  CLIENT_PROFILE_FIELDS,
  CLIENT_PROFILE_GRID_METADATA,
  CLIENT_ROLE_FIELDS,
  CLIENT_ROLE_EDIT_FIELDS,
  CLIENT_ROLE_GRID_METADATA,
  CLIENT_SECURITY_ITEM_NAMES,
  CLIENT_SECURITY_OPERATIONS,
  DEFAULT_CLIENT_PROFILE_LOCALE,
  DEFAULT_CLIENT_ROLE_PRECEDENCE,
  DIRECTORY_USER_CARD_METADATA,
  GUEST_ACCESS_CONFIGURATION_FIELDS,
  GUEST_INVITATION_CARD_METADATA,
  GUEST_PROFILE_CANDIDATE_CARD_METADATA,
  MINIMUM_CLIENT_ROLE_PRECEDENCE,
  NO_PERMISSION_SET_VALUE,
  cloneSecurityValue,
  createSecurityRequest,
  mergeClientProfileDraftChange,
  normalizeClientProfileAddress,
  permissionSetFormFields,
  permissionSetRoleDefaults,
  profileRoleFormFields,
  selectSecurityFields,
} from './clientSecurityContracts';

const CLIENT_ADMIN_ROLE_CODE = 'ClientAdmin';

const runOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: true,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

const ROLE_PERMISSION_CAPABILITIES = Object.freeze([
  'CanView',
  'CanCreate',
  'CanEdit',
  'CanDelete',
  'CanHardDelete',
  'CanExecute',
  'CanAccess',
  'CanViewOnNav',
]);
const ROLE_PERMISSION_DISABLE_FIELDS = Object.freeze({
  CanView: 'DisableView',
  CanCreate: 'DisableCreate',
  CanEdit: 'DisableEdit',
  CanDelete: 'DisableDelete',
  CanHardDelete: 'DisableHardDelete',
  CanExecute: 'DisableExecute',
  CanAccess: 'DisableAccess',
  CanViewOnNav: 'DisableViewOnNav',
});

const extractPermissionEntries = (value, depth = 0) => {
  if (depth > 6 || value == null) return null;
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (Array.isArray(parsed)) {
    return parsed.some((entry) => entry?.Permission) ? parsed : null;
  }
  if (typeof parsed !== 'object') return null;
  if (Array.isArray(parsed.Entries)) return parsed.Entries;
  for (const key of ['permissionSets', 'permissions', 'output', 'data', 'value', 'payload']) {
    const entries = extractPermissionEntries(parsed[key], depth + 1);
    if (entries) return entries;
  }
  return null;
};

const samePermissionCapabilities = (left, right) => (
  ROLE_PERMISSION_CAPABILITIES.every((key) => (
    (left?.[key] === true) === (right?.[key] === true)
  ))
);

const hasGrantedRoleCapability = (entry) => (
  ROLE_PERMISSION_CAPABILITIES.some((key) => entry?.[key] === true)
);

const toRolePermissionEntry = (permission) => ({
  Permission: permission.Permission,
  ...ROLE_PERMISSION_CAPABILITIES.reduce((result, key) => ({
    ...result,
    [key]: permission[key] === true,
  }), {}),
  ApplyDependencies: permission.ApplyDependencies !== false,
});

const expandRolePermissionDependencies = (entries, permissionCatalog) => {
  const catalog = new Map((permissionCatalog || []).map((permission) => (
    [String(permission.Permission).toLowerCase(), permission]
  )));
  const expanded = new Map((entries || []).map((entry) => (
    [String(entry.Permission).toLowerCase(), { ...entry }]
  )));
  (entries || []).forEach((entry) => {
    if (entry?.ApplyDependencies === false) return;
    const permission = catalog.get(String(entry?.Permission).toLowerCase());
    Object.entries(permission?.PermissionDependencies || {}).forEach(([
      capability,
      dependencyPermissions,
    ]) => {
      if (!ROLE_PERMISSION_CAPABILITIES.includes(capability)) return;
      (dependencyPermissions || []).forEach((dependencyPermission) => {
        const key = String(dependencyPermission).toLowerCase();
        const dependencyCatalog = catalog.get(key);
        if (!dependencyCatalog) return;
        const dependencyEntry = expanded.get(key) || {
          Permission: dependencyCatalog.Permission,
          ...ROLE_PERMISSION_CAPABILITIES.reduce((result, fieldName) => ({
            ...result,
            [fieldName]: dependencyCatalog[fieldName] === true,
          }), {}),
          ApplyDependencies: false,
        };
        if (dependencyCatalog[ROLE_PERMISSION_DISABLE_FIELDS[capability]] !== true) {
          dependencyEntry[capability] = true;
        }
        expanded.set(key, dependencyEntry);
      });
    });
  });
  return Array.from(expanded.values());
};

const roleDetailsChanged = (baseline, draft) => (
  CLIENT_ROLE_EDIT_FIELDS.some((fieldName) => (
    String(baseline?.[fieldName] ?? '') !== String(draft?.[fieldName] ?? '')
  ))
);

const isUserCancelledLogin = (error) => {
  const code = String(
    error?.errorCode
      || error?.code
      || '',
  ).trim().toLowerCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'user_cancelled'
    || message.includes('user_cancelled')
    || message.includes('user cancelled');
};

const SecurityState = ({
  loading,
  error,
  message,
  label,
}) => {
  if (loading) {
    return (
      <ResourceStateControl
        compact
        loading
        title={`Loading ${label}`}
        loadingText={`Loading ${label}.`}
      />
    );
  }
  if (error) {
    return (
      <ResourceStateControl
        compact
        tone="error"
        title={`${label} unavailable`}
        message={error}
      />
    );
  }
  return message ? (
    <ResourceStateControl
      compact
      title={`${label} status`}
      message={message}
      role="status"
    />
  ) : null;
};

const SecurityToolbar = ({
  busy,
  onRefresh,
  actions = [],
  ariaLabel,
  primaryActionFirst = false,
  spacious = false,
}) => {
  const refreshAction = {
    key: 'refresh-client-security-screen',
    label: 'Refresh',
    iconKey: 'refresh',
    disabled: busy,
    onClick: onRefresh,
  };
  const orderedActions = primaryActionFirst && actions.length > 0
    ? [actions[0], refreshAction, ...actions.slice(1)]
    : [refreshAction, ...actions];
  return (
    <ConvokeCommandBar
      actions={orderedActions}
      ariaLabel={ariaLabel}
      className={[
        'convoke-command-bar--screen',
        spacious ? 'command-security__toolbar' : '',
      ].filter(Boolean).join(' ')}
      useSparkButtons
    />
  );
};

const securityStatusTone = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized
    || normalized.startsWith('not ')
    || normalized.includes('disabled')) {
    return 'muted';
  }
  if (/(error|fail|invalid|unavailable|blocked|offline)/.test(normalized)) {
    return 'error';
  }
  if (/(warning|pending|attention|progress)/.test(normalized)) {
    return 'warning';
  }
  if (/(validated|verified|ready|configured|enabled|healthy|active|online|success)/.test(normalized)) {
    return 'success';
  }
  return 'muted';
};

const SecurityStatusTag = ({ status }) => (
  status ? (
    <span
      className={`resource-workspace-status-pill resource-workspace-status-pill--${securityStatusTone(status)}`}
    >
      {status}
    </span>
  ) : null
);

const StatusSection = ({
  title,
  mode,
  status,
  children,
}) => (
  <section className="resource-workspace-panel">
    <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
      <div>
        <h2>{title}</h2>
        {mode ? <p>{mode}</p> : null}
      </div>
      <SecurityStatusTag status={status} />
    </div>
    <div className="command-security__status-body">{children}</div>
  </section>
);

const formatLocalDateTime = (value, fallback = 'Not configured') => {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? fallback
    : date.toLocaleString();
};

const StatusValue = ({ label, value }) => (
  <div className="resource-workspace-metric">
    <span>{label}</span>
    <strong>{value || 'Not configured'}</strong>
  </div>
);

const ENTRA_PROVIDER_SETUP_FIELD_PRESENTATION = Object.freeze({
  DisplayName: Object.freeze({
    Description: 'Name used to distinguish this provider mapping.',
  }),
  TenantId: Object.freeze({
    Description: 'Optional for Entra Setup. Enter an existing tenant ID only when it should be reused.',
  }),
  TenantDomain: Object.freeze({
    Description: 'Optional for Entra Setup. The script resolves the tenant’s initial verified domain.',
  }),
  SignInClientId: Object.freeze({
    Description: 'Optional. Leave blank for Entra Setup to create the sign-in application.',
  }),
  ReadClientId: Object.freeze({
    Description: 'Optional. Leave blank for Entra Setup to create the Graph Read application.',
  }),
  ReadClientSecretInput: Object.freeze({
    Description: 'Optional for Entra Setup. Supply a write-only secret only when saving the provider manually.',
  }),
});

const ENTRA_PROVIDER_DIRECT_SAVE_FIELDS = Object.freeze([
  'DisplayName',
  'TenantId',
  'TenantDomain',
  'SignInClientId',
  'ReadClientId',
  'ReadClientSecretInput',
]);

const hasCompleteProviderConnection = (item) => (
  ENTRA_PROVIDER_DIRECT_SAVE_FIELDS.every((fieldName) => (
    String(item?.[fieldName] || '').trim().length > 0
  ))
);

const ClientEntraProviderSetupPanel = ({
  open,
  metadata,
  formFields,
  itemData,
  canWrite,
  busy,
  providerSetupIssued,
  setupComplete,
  statusMessage,
  onSave,
  onCancel,
  onClientEmail,
  onEntraSetup,
  onRefresh,
}) => {
  const [width, setWidth] = useState(460);
  const contentRef = useRef(null);
  return (
    <SidePanel
      childrenRef={contentRef}
      open={open}
      fieldName="ClientEntraProviderSetupWidth"
      onUpdateSidePanel={() => {}}
      updateSidePanelWidth={setWidth}
      width={metadata?.SidePanelFormWidth}
      isAutoWidth
      onCancel={onCancel}
    >
      <FormControl
        ref={contentRef}
        metadata={metadata}
        className="command-security__provider-add-form"
        title="Add Microsoft Entra ID"
        formFields={formFields}
        itemData={itemData}
        width={width}
        onSave={onSave}
        onCancel={onCancel}
        viewOnly={!canWrite || providerSetupIssued}
        saveButtonText="Add Provider"
        customFormFooter={(updatedItem) => (
          <div className="command-security__provider-add-footer">
            <ClientEntraSetupGuidance
              canWrite={canWrite}
              busy={busy}
              setupComplete={setupComplete}
              onClientEmail={() => onClientEmail(updatedItem)}
              onEntraSetup={() => onEntraSetup(updatedItem)}
              onRefresh={onRefresh}
              refreshEnabled={providerSetupIssued}
              statusMessage={statusMessage}
            />
            <div className="command-security__provider-add-actions">
              {providerSetupIssued ? (
                <ButtonControl
                  type="okay"
                  onClick={onCancel}
                  disabled={busy}
                >
                  Close
                </ButtonControl>
              ) : (
                <>
                  <ButtonControl
                    type="cancel"
                    onClick={onCancel}
                  >
                    Cancel
                  </ButtonControl>
                  <ButtonControl
                    type="okay"
                    onClick={() => onSave(updatedItem)}
                    disabled={
                      !canWrite
                      || busy
                      || !hasCompleteProviderConnection(updatedItem)
                    }
                  >
                    Add Provider
                  </ButtonControl>
                </>
              )}
            </div>
          </div>
        )}
      />
    </SidePanel>
  );
};

const ClientProfileFormPanel = ({
  open,
  title,
  metadata,
  formFields,
  itemData,
  onChange,
  onSave,
  onCancel,
  setItemData,
  canWrite,
  busy,
}) => {
  const [width, setWidth] = useState(460);
  const contentRef = useRef(null);
  const closePanel = () => {
    onCancel();
    setItemData(null);
  };

  return (
    <SidePanel
      childrenRef={contentRef}
      open={open}
      fieldName="ClientUserProfileFormWidth"
      onUpdateSidePanel={() => {}}
      updateSidePanelWidth={setWidth}
      width={metadata?.SidePanelFormWidth}
      isAutoWidth
      onCancel={closePanel}
    >
      <FormControl
        ref={contentRef}
        metadata={metadata}
        className="command-security__profile-form"
        title={title}
        formFields={formFields}
        itemData={itemData}
        onChange={onChange}
        width={width}
        onSave={onSave}
        onCancel={closePanel}
        disabled={busy}
        viewOnly={!canWrite}
        customFormFooter={(updatedItem) => (
          <>
            <ButtonControl
              type="cancel"
              onClick={closePanel}
              disabled={busy}
            >
              Cancel
            </ButtonControl>
            {canWrite ? (
              <ButtonControl
                type="okay"
                onClick={() => onSave(updatedItem)}
                disabled={busy}
                loading={busy}
              >
                Save
              </ButtonControl>
            ) : null}
          </>
        )}
      />
    </SidePanel>
  );
};

const useMutableSecurityGridMetadata = (metadata) => useMemo(
  () => [cloneSecurityValue(metadata)],
  [metadata],
);

const GUEST_READINESS_MESSAGES = Object.freeze({
  ExternalAccessEnvironmentNotReady:
    'The hosted External Access environment is not ready for Guest sign-in.',
  ExternalTenantMissing:
    'The External Access tenant has not been configured.',
  ExternalTenantConfigurationUnsupported:
    'This environment is configured with a customer/CIAM tenant. Guest access requires a Microsoft Entra workforce tenant configured for B2B collaboration.',
  ExternalTenantLockMismatch:
    'The configured External Access tenant does not match this environment.',
  GuestApiScopeMissing:
    'The Guest application API permission has not been configured.',
  GuestApplicationMissing:
    'The Guest sign-in application has not been configured.',
  GuestDomainNotReady:
    'The application domain is not verified and ready for Guest sign-in.',
  GuestGroupIdentityMissing:
    'The identity used to manage Guest access groups has not been configured.',
  GuestInvitationIdentityMissing:
    'The identity used to send Guest invitations has not been configured.',
  GuestRedirectNotVerified:
    'The Guest sign-in callback address has not been verified.',
});

const getGuestReadinessMessage = (code) => {
  if (GUEST_READINESS_MESSAGES[code]) return GUEST_READINESS_MESSAGES[code];
  const readable = String(code || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  return readable
    ? `${readable} requires additional setup.`
    : 'Additional Guest access setup is required.';
};

const getGuestResultMessage = (message) => {
  const text = String(message || '');
  const containsReadinessCode = Object.keys(GUEST_READINESS_MESSAGES)
    .some((code) => text.includes(code));
  return containsReadinessCode
    ? 'Guest access cannot be enabled yet. Complete the setup requirements shown above the configuration form.'
    : message;
};

const getGuestInvitationResultMessage = (message) => {
  const text = String(message || '');
  const containsReadinessCode = Object.keys(GUEST_READINESS_MESSAGES)
    .some((code) => text.includes(code));
  return containsReadinessCode
    ? 'Guest invitations are unavailable until Guest / External Access setup is complete in Authentication.'
    : message;
};

const GUEST_ENVIRONMENT_SETUP_BLOCKERS = new Set([
  'ExternalAccessEnvironmentNotReady',
  'ExternalTenantMissing',
  'ExternalTenantConfigurationUnsupported',
  'ExternalTenantLockMismatch',
  'GuestApiScopeMissing',
  'GuestApplicationMissing',
  'GuestGroupIdentityMissing',
  'GuestInvitationIdentityMissing',
]);

const buildGuestReadinessTasks = (blockers, currentEnvironment) => {
  const blockerSet = new Set(blockers || []);
  const tasks = [];
  const consumed = new Set();
  if ([...GUEST_ENVIRONMENT_SETUP_BLOCKERS]
    .some((code) => blockerSet.has(code))) {
    [...GUEST_ENVIRONMENT_SETUP_BLOCKERS].forEach((code) => consumed.add(code));
    const local = String(currentEnvironment || '').toUpperCase() === 'LOCAL';
    const wrongTenantType = blockerSet.has(
      'ExternalTenantConfigurationUnsupported',
    );
    tasks.push({
      key: 'environment',
      title: 'Platform External Access',
      message: wrongTenantType
        ? 'The configured tenant is a customer/CIAM directory. Convoke Guest access uses a separate Microsoft Entra workforce tenant with B2B collaboration so work accounts, personal Microsoft accounts, and email one-time passcode guests can use Microsoft-hosted sign-in.'
        : local
        ? 'Your Azure tenant is not selected from this client screen. LOCAL uses the hosted DEV External Access setup for Azure acceptance testing; a deployment administrator must reconcile that environment first.'
        : `A deployment administrator must reconcile Platform External Access for ${currentEnvironment || 'this environment'}. That process creates or reuses the Convoke-owned External ID resources and validates the Guest API, sign-in application, permissions, and purpose-scoped identities.`,
      action: 'deployment',
      actionLabel: 'How to configure',
    });
  }
  if (blockerSet.has('GuestDomainNotReady')) {
    consumed.add('GuestDomainNotReady');
    tasks.push({
      key: 'domain',
      title: 'Application domain',
      message: 'Add or validate the client domain used for Guest sign-in, then return here to refresh readiness.',
      action: 'domains',
      actionLabel: 'Open Domains',
    });
  }
  if (blockerSet.has('GuestRedirectNotVerified')) {
    consumed.add('GuestRedirectNotVerified');
    tasks.push({
      key: 'callback',
      title: 'Guest callback',
      message: 'Review the canonical origin and callback in Configuration. The hosted deployment reconciler must also verify the callback.',
    });
  }
  [...blockerSet]
    .filter((code) => !consumed.has(code))
    .forEach((code) => tasks.push({
      key: code,
      title: 'Additional setup',
      message: getGuestReadinessMessage(code),
    }));
  return tasks;
};

const ClientGuestAccessPanel = ({
  open,
  workspace,
  result,
  draft,
  setDraft,
  canSave,
  busy,
  onClose,
  onOpenDomains,
  onSaveMode,
}) => {
  const [deploymentHelpOpen, setDeploymentHelpOpen] = useState(false);
  useEffect(() => {
    if (open) {
      setDeploymentHelpOpen(false);
    }
  }, [open]);
  const update = (fieldName, value) => setDraft((current) => ({
    ...(current || {}),
    [fieldName]: value,
  }));
  const readinessBlockers = Array.isArray(workspace?.ReadinessBlockers)
    ? workspace.ReadinessBlockers
    : [];
  const readinessTasks = buildGuestReadinessTasks(
    readinessBlockers,
    workspace?.CurrentEnvironment,
  );
  const currentEnvironment = String(
    workspace?.CurrentEnvironment || '',
  ).toUpperCase();
  const deploymentEnvironment = ['LOCAL', 'DEV', 'QA', 'STAGE', 'PROD']
    .includes(currentEnvironment)
    ? currentEnvironment
    : 'LOCAL';

  return (
    <PanelControl
      open={open}
      title="Guest / External Access"
      onClose={onClose}
      mapToClass="core-layout"
      position="right"
      width={760}
      minWidth={480}
      maxWidth={960}
      storageKey="client-command-guest-access-panel-width"
      className="command-security__guest-panel"
      contentClassName="command-security__guest-panel-content"
      footer={(
        <>
          {canSave ? (
            <ButtonControl
              type="okay"
              onClick={onSaveMode}
              disabled={
                busy
                || (
                  draft?.GuestAccessActive === true
                  && workspace?.ReadyForActivation !== true
                )
              }
            >
              Save
            </ButtonControl>
          ) : null}
          <ButtonControl type="cancel" onClick={onClose} disabled={busy}>
            Close
          </ButtonControl>
        </>
      )}
    >
      <div className="command-security__guest-content">
        <p className="command-security__guest-intro">
          Configure the Convoke-hosted Guest provider for this application.
        </p>
        <ConvokeDialogMessage
          message={getGuestResultMessage(result?.message)}
          tone={result?.tone}
        />
        {readinessBlockers.length > 0 ? (
          <div
            className="command-security__guest-readiness-guidance"
            role="status"
          >
            <p>
              Complete these items before activating Guest access:
            </p>
            <div className="command-security__guest-readiness-tasks">
              {readinessTasks.map((task) => (
                <div
                  className="command-security__guest-readiness-task"
                  key={task.key}
                >
                  <div>
                    <strong>{task.title}</strong>
                    <p>{task.message}</p>
                  </div>
                  {task.action ? (
                    <ButtonControl
                      type="standard-small"
                      onClick={() => {
                        if (task.action === 'domains') {
                          onOpenDomains?.();
                        } else if (task.action === 'deployment') {
                          setDeploymentHelpOpen(true);
                        }
                      }}
                    >
                      {task.actionLabel}
                    </ButtonControl>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div className="command-security__guest-fields">
          {GUEST_ACCESS_CONFIGURATION_FIELDS.map((field) => (
            <FieldControl
              key={field.FieldName}
              fieldMetadata={field}
              value={draft?.[field.FieldName]}
              onChange={(value) => update(field.FieldName, value)}
              disabled={!canSave || busy}
              disableError
            />
          ))}
        </div>
        <DialogControl
          openDialog={deploymentHelpOpen}
          title="Configure Platform External Access"
          subTitle="This is a platform-environment setup, not a setting on one client application."
          okText="Close"
          onOk={() => setDeploymentHelpOpen(false)}
          onCancel={() => setDeploymentHelpOpen(false)}
          hideCancel
          hideCancelButton
        >
          <div className="command-security__guest-deployment-help">
            <p>
              Do not paste an Azure tenant ID into this client screen. Convoke
              uses one owned and verified Microsoft Entra workforce B2B tenant
              topology for each hosted environment. Customer/CIAM directories
              are a different identity model and are rejected by Guest mode.
            </p>
            {currentEnvironment === 'LOCAL' ? (
              <p>
                LOCAL maps to the shared DEV workforce B2B authority. The
                deployment owner must configure that workforce tenant before
                reconciliation; LOCAL then receives a reference to the same
                authority and does not create a second tenant.
              </p>
            ) : null}
            <p>1. From the deployment workspace, run this read-only check:</p>
            <code>
              {`CoreCLI.exe --core -ensure-convoke-external-access PlatformApp ${deploymentEnvironment}`}
            </code>
            <p>
              2. Review the redacted plan. Under the required environment
              approval, the deployment owner can run the same command with
              <code> -execute 1</code>.
            </p>
            <p>
              3. Return here and refresh Authentication. Create Solution and
              Update Solution use this same reconciler for new and existing
              hosted environments.
            </p>
          </div>
        </DialogControl>
      </div>
    </PanelControl>
  );
};

const getGuestInvitationCardStatus = (invitation) => {
  const title = String(invitation?.Status || 'Not linked').trim() || 'Not linked';
  const tone = title === 'Logged in'
    ? 'success'
    : title === 'Linked'
      ? 'active'
      : title === 'Profile created'
        ? 'configured'
        : 'inactive';
  return { title, tone };
};

const getGuestInvitationCardTitle = (invitation) => {
  const name = String(invitation?.DisplayName || invitation?.Email || 'Guest').trim();
  return `${name} — ${getGuestInvitationCardStatus(invitation).title}`;
};

const ClientGuestInvitationsPanel = ({
  open,
  clientId,
  appId,
  currentEnvironment,
  canLoad,
  canInvite,
  canReconcile,
  canRevoke,
  onClose,
  onSuccess,
  onActionError,
}) => {
  const manager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.guestAccessWorkspace,
  );
  const managerRef = useRef(manager);
  const [activeTab, setActiveTab] = useState('invite');
  const [workspace, setWorkspace] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [selectedInvitationIds, setSelectedInvitationIds] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const invitationMetadata = useMutableSecurityGridMetadata(
    GUEST_INVITATION_CARD_METADATA,
  );
  const candidateMetadata = useMutableSecurityGridMetadata(
    GUEST_PROFILE_CANDIDATE_CARD_METADATA,
  );
  managerRef.current = manager;

  const applyWorkspace = useCallback((updated) => {
    setWorkspace(updated);
    setSelectedCandidateIds([]);
    setSelectedInvitationIds([]);
    setCandidateSearch('');
  }, []);

  const loadInvitations = useCallback(async () => {
    if (!canLoad) return;
    setBusy(true);
    setResult(null);
    try {
      const loaded = await runOperation(
        managerRef.current,
        CLIENT_SECURITY_OPERATIONS.loadGuestAccess,
        createSecurityRequest({
          workspace: null,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Guest invitations are unavailable.',
      );
      applyWorkspace(loaded);
    } catch (ex) {
      setWorkspace(null);
      setResult({
        message: ex?.message || 'Guest invitations are unavailable.',
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  }, [appId, applyWorkspace, canLoad, clientId, currentEnvironment]);

  useEffect(() => {
    if (open) {
      setActiveTab('invite');
      loadInvitations();
    } else {
      setResult(null);
      setSelectedCandidateIds([]);
      setSelectedInvitationIds([]);
      setCandidateSearch('');
    }
  }, [loadInvitations, open]);

  const execute = async (
    operationName,
    overrides,
    failureMessage,
  ) => {
    setBusy(true);
    setResult(null);
    try {
      const updated = await runOperation(
        managerRef.current,
        operationName,
        createSecurityRequest({
          workspace,
          clientId,
          appId,
          currentEnvironment,
          overrides,
        }),
        failureMessage,
      );
      applyWorkspace(updated);
      onSuccess(updated?.StatusMessage || 'Guest invitations updated.');
      return updated;
    } catch (ex) {
      onActionError(
        'Guest invitation action failed',
        ex?.message || failureMessage,
      );
      return null;
    } finally {
      setBusy(false);
    }
  };

  const active = workspace?.GuestAccessActive === true
    && workspace?.ReadyForActivation === true;
  const inviteReady = active
    && canInvite
    && canReconcile
    && workspace?.CanManageInvitations === true
    && selectedCandidateIds.length > 0;
  const invitationDisabled = !canInvite
    || !canReconcile
    || busy
    || !active
    || workspace?.CanManageInvitations !== true;
  const inviteCandidates = workspace?.InviteCandidates || [];
  const filteredInviteCandidates = useMemo(() => {
    const normalizedSearch = candidateSearch.trim().toLowerCase();
    if (!normalizedSearch) return inviteCandidates;
    return inviteCandidates.filter((candidate) => (
      [
        candidate.DisplayName,
        candidate.Email,
        candidate.AssignedRoles,
      ].some((value) => String(value || '')
        .toLowerCase()
        .includes(normalizedSearch))
    ));
  }, [candidateSearch, inviteCandidates]);
  const filteredCandidateIds = filteredInviteCandidates
    .map((candidate) => candidate.Id);
  const allFilteredCandidatesSelected = filteredCandidateIds.length > 0
    && filteredCandidateIds.every((candidateId) => (
      selectedCandidateIds.includes(candidateId)
    ));
  const selectedInvitation = (workspace?.Invitations || []).find(
    (invitation) => selectedInvitationIds.includes(invitation.Id),
  ) || null;
  const resendReady = selectedInvitationIds.length === 1
    && selectedInvitation?.CanResend === true;
  const queueInvitation = async () => {
    if (!canInvite || !canReconcile) return;
    setBusy(true);
    setResult(null);
    try {
      const selectedCandidates = (workspace?.InviteCandidates || [])
        .filter((candidate) => selectedCandidateIds.includes(candidate.Id));
      const queued = await runOperation(
        managerRef.current,
        CLIENT_SECURITY_OPERATIONS.inviteGuest,
        createSecurityRequest({
          workspace,
          clientId,
          appId,
          currentEnvironment,
          overrides: {
            InviteProfileIds: selectedCandidateIds,
          },
        }),
        'Guest invitation could not be queued.',
      );
      const updated = await runOperation(
        managerRef.current,
        CLIENT_SECURITY_OPERATIONS.reconcileGuestAccess,
        createSecurityRequest({
          workspace: queued,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Microsoft could not send the Guest invitation.',
      );
      const sentEmails = new Set((updated?.Invitations || [])
        .filter((invitation) => invitation.DeliveryStatus === 'Sent by Microsoft')
        .map((invitation) => String(invitation.Email || '').trim().toLowerCase())
        .filter(Boolean));
      const unsentCandidates = selectedCandidates.filter((candidate) => (
        !sentEmails.has(String(candidate.Email || '').trim().toLowerCase())
      ));
      if (unsentCandidates.length > 0) {
        throw new Error(
          'Microsoft did not confirm invitation delivery for one or more selected profiles. Review Invitations before retrying.',
        );
      }
      applyWorkspace(updated);
      setActiveTab('invitations');
      onSuccess(selectedCandidates.length === 1
        ? 'Microsoft sent the Guest invitation email.'
        : `Microsoft sent ${selectedCandidates.length.toLocaleString()} Guest invitation emails.`);
    } catch (ex) {
      onActionError(
        'Guest invitation could not be sent',
        ex?.message || 'Microsoft could not send the Guest invitation.',
      );
    } finally {
      setBusy(false);
    }
  };

  const resendInvitation = async () => {
    if (!canInvite || !canReconcile || !selectedInvitation) return;
    setBusy(true);
    setResult(null);
    try {
      const previousSentAt = Date.parse(
        selectedInvitation.InvitationMessageSentAtUtc || '',
      );
      const queued = await runOperation(
        managerRef.current,
        CLIENT_SECURITY_OPERATIONS.inviteGuest,
        createSecurityRequest({
          workspace,
          clientId,
          appId,
          currentEnvironment,
          overrides: {
            SelectedInvitationId: selectedInvitation.Id,
            InviteProfileIds: [],
          },
        }),
        'Guest invitation resend could not be queued.',
      );
      const updated = await runOperation(
        managerRef.current,
        CLIENT_SECURITY_OPERATIONS.reconcileGuestAccess,
        createSecurityRequest({
          workspace: queued,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Microsoft could not resend the Guest invitation.',
      );
      const resentInvitation = (updated?.Invitations || []).find(
        (invitation) => invitation.Id === selectedInvitation.Id,
      );
      const resentAt = Date.parse(
        resentInvitation?.InvitationMessageSentAtUtc || '',
      );
      if (!Number.isFinite(resentAt)
        || (Number.isFinite(previousSentAt) && resentAt <= previousSentAt)
        || resentInvitation?.AcceptanceStatus !== 'Awaiting acceptance') {
        throw new Error(
          'Microsoft did not confirm that a new Guest invitation email was sent.',
        );
      }
      applyWorkspace(updated);
      onSuccess(Number.isFinite(previousSentAt)
        ? 'Microsoft resent the Guest invitation email.'
        : 'Microsoft sent the Guest invitation email.');
    } catch (ex) {
      onActionError(
        'Guest invitation could not be resent',
        ex?.message || 'Microsoft could not resend the Guest invitation.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelControl
      open={open}
      title="Guest Invitations"
      onClose={onClose}
      mapToClass="core-layout"
      position="right"
      width={760}
      minWidth={480}
      maxWidth={960}
      storageKey="client-command-guest-invitations-panel-width"
      className="command-security__guest-panel"
      contentClassName="command-security__guest-panel-content"
      footer={(
        <>
          {activeTab === 'invite' ? (
            canInvite && canReconcile ? (
              <ButtonControl
                type="okay"
                onClick={queueInvitation}
                disabled={busy || !inviteReady}
              >
                Invite Guest
              </ButtonControl>
            ) : null
          ) : (
            <>
              {canInvite && canReconcile ? (
                <ButtonControl
                  type="okay"
                  onClick={resendInvitation}
                  disabled={invitationDisabled || !resendReady}
                >
                  Resend Invitation
                </ButtonControl>
              ) : null}
              {canReconcile ? (
                <ButtonControl
                  type="standard"
                  onClick={() => execute(
                    CLIENT_SECURITY_OPERATIONS.reconcileGuestAccess,
                    {},
                    'Guest access reconciliation failed.',
                  )}
                  disabled={busy || !active}
                >
                  Reconcile
                </ButtonControl>
              ) : null}
              {canRevoke ? (
                <ButtonControl
                  type="cancel"
                  onClick={() => execute(
                    CLIENT_SECURITY_OPERATIONS.revokeGuest,
                    { SelectedInvitationId: selectedInvitationIds[0] || '' },
                    'Guest access could not be revoked.',
                  )}
                  disabled={
                    busy
                    || workspace?.CanRevokeInvitations !== true
                    || selectedInvitationIds.length !== 1
                  }
                >
                  Revoke Selected
                </ButtonControl>
              ) : null}
            </>
          )}
          <ButtonControl type="cancel" onClick={onClose} disabled={busy}>
            Close
          </ButtonControl>
        </>
      )}
    >
      <div className="command-security__guest-content">
        <p className="command-security__guest-intro">
          Invite Guests and track each person from profile creation through
          their first Microsoft sign-in. Microsoft sends the redemption email,
          so a separate Convoke email service is not required.
        </p>
        <ConvokeDialogMessage
          message={getGuestInvitationResultMessage(result?.message)}
          tone={result?.tone}
        />
        {workspace ? (
          <TabPaneControl
            activeKey={activeTab}
            align="right"
            framed={false}
            className="command-security__guest-tabs"
            onChange={(_index, tab) => {
              setActiveTab(tab?.key || 'invite');
              setSelectedCandidateIds([]);
              setSelectedInvitationIds([]);
              setCandidateSearch('');
            }}
            unmountInactive
            tabs={[
              {
                key: 'invite',
                title: 'Invite Guest',
                content: (
                  <div className="command-security__guest-invite-list">
                    <p>
                      Search existing profiles and select everyone to invite.
                      Email, display name, and assigned roles come directly
                      from each profile. Profiles that already received a
                      Microsoft Guest invitation are not shown.
                    </p>
                    <CardGridControl
                      rows={filteredInviteCandidates}
                      metadata={candidateMetadata}
                      cardFields={candidateMetadata[0].Fields}
                      rowKey={(profile) => profile.Id}
                      selectable
                      multiSelect
                      selectedRowKeys={selectedCandidateIds}
                      onSelectedRowKeysChange={setSelectedCandidateIds}
                      searchable={false}
                      readOnly={false}
                      disabled={invitationDisabled}
                      disableInlineEdit
                      disableCardClickEdit
                      activeFilterEnabled={false}
                      toolbarLeadingContent={(
                        <div className="command-security__guest-candidate-toolbar-leading">
                          <SearchInput
                            className="command-security__guest-candidate-search"
                            value={candidateSearch}
                            onChange={setCandidateSearch}
                            placeholder="Search profiles..."
                            disabled={invitationDisabled}
                          />
                          <span className="command-security__guest-candidate-selection-count">
                            {selectedCandidateIds.length.toLocaleString()}
                            {' selected'}
                          </span>
                        </div>
                      )}
                      toolbarActions={[{
                        key: 'select-all-candidates',
                        label: 'Select All',
                        title: 'Select all matching profiles',
                        type: 'okay-small',
                        disabled: invitationDisabled
                          || filteredCandidateIds.length === 0
                          || allFilteredCandidatesSelected,
                        onClick: () => setSelectedCandidateIds((current) => (
                          Array.from(new Set([
                            ...current,
                            ...filteredCandidateIds,
                          ]))
                        )),
                      }]}
                      emptyContent="No profiles are available to invite. Profiles that already received a Microsoft Guest invitation do not appear here."
                      minCardWidth="100%"
                      maxColumns={1}
                    />
                  </div>
                ),
              },
              {
                key: 'invitations',
                title: 'Invitations',
                content: (
                  <div className="command-security__guest-invitations-list">
                    <p>
                      Status appears beside each person&apos;s name. Assigned
                      roles come from the current profile. Before normal
                      sign-in, a pending person must open Microsoft&apos;s email
                      and accept the Guest invitation.
                    </p>
                    {(workspace?.Invitations || []).length > 0 ? (
                      <CardGridControl
                        rows={workspace.Invitations}
                        metadata={invitationMetadata}
                        cardFields={invitationMetadata[0].Fields}
                        cardTitleResolver={getGuestInvitationCardTitle}
                        headerStatusResolver={getGuestInvitationCardStatus}
                        rowKey={(invitation) => invitation.Id}
                        selectable
                        selectedRowKeys={selectedInvitationIds}
                        onSelectedRowKeysChange={setSelectedInvitationIds}
                        readOnly={false}
                        disabled={busy}
                        disableInlineEdit
                        disableCardClickEdit
                        activeFilterEnabled={false}
                        minCardWidth="100%"
                        maxColumns={1}
                      />
                    ) : (
                      <p className="command-security__guest-empty">
                        No pending Guest access records. Completed and revoked
                        access remains available in User Profiles and Activity
                        &amp; Logs.
                      </p>
                    )}
                  </div>
                ),
              },
            ]}
          />
        ) : null}
      </div>
    </PanelControl>
  );
};

export const ClientAuthenticationScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const navigate = useNavigate();
  const { canExecute } = useCorePermissions();
  const workspaceManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.authenticationWorkspace,
  );
  const connectionManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.connectionForm,
  );
  const mappingManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.mappingForm,
  );
  const azureMapsManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.azureMapsForm,
  );
  const guestManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.guestAccessWorkspace,
  );
  const setupScriptManager = useGenericManager(
    CLIENT_ENTRA_SETUP_SCRIPT_ITEM_NAME,
  );
  const workspaceRef = useRef(workspaceManager);
  const connectionRef = useRef(connectionManager);
  const mappingRef = useRef(mappingManager);
  const azureMapsRef = useRef(azureMapsManager);
  const guestRef = useRef(guestManager);
  const setupScriptRef = useRef(setupScriptManager);
  const [workspace, setWorkspace] = useState(null);
  const [connectionMetadata, setConnectionMetadata] = useState(null);
  const [mappingMetadata, setMappingMetadata] = useState(null);
  const [azureMapsMetadata, setAzureMapsMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [operationResult, setOperationResult] = useState(null);
  const [providerSelectionOpen, setProviderSelectionOpen] = useState(false);
  const [selectedProviderKeys, setSelectedProviderKeys] = useState([]);
  const [selectedMappingId, setSelectedMappingId] = useState('');
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [connectionDraft, setConnectionDraft] = useState(null);
  const [providerSetupIssued, setProviderSetupIssued] = useState(false);
  const [
    providerSetupBaselineValidationAt,
    setProviderSetupBaselineValidationAt,
  ] = useState('');
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingDraft, setMappingDraft] = useState(null);
  const [pendingDeleteMapping, setPendingDeleteMapping] = useState(null);
  const [instructionsOpen, setInstructionsOpen] = useState(false);
  const [entraEmailOpen, setEntraEmailOpen] = useState(false);
  const [azureMapsOpen, setAzureMapsOpen] = useState(false);
  const [azureMapsDraft, setAzureMapsDraft] = useState(null);
  const [removeAzureMapsOpen, setRemoveAzureMapsOpen] = useState(false);
  const [resetProtectedConnectionOpen, setResetProtectedConnectionOpen] = useState(false);
  const [pendingMappingStatus, setPendingMappingStatus] = useState(null);
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestWorkspace, setGuestWorkspace] = useState(null);
  const [guestDraft, setGuestDraft] = useState(null);
  const [guestResult, setGuestResult] = useState(null);
  workspaceRef.current = workspaceManager;
  connectionRef.current = connectionManager;
  mappingRef.current = mappingManager;
  azureMapsRef.current = azureMapsManager;
  guestRef.current = guestManager;
  setupScriptRef.current = setupScriptManager;

  const request = useCallback(
    (overrides = {}) => createSecurityRequest({
      workspace,
      clientId,
      appId,
      currentEnvironment,
      overrides,
    }),
    [appId, clientId, currentEnvironment, workspace],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        loaded,
        connectionResult,
        mappingResult,
        azureMapsResult,
      ] = await Promise.all([
        runOperation(
          workspaceRef.current,
          CLIENT_SECURITY_OPERATIONS.loadAuthentication,
          createSecurityRequest({
            workspace: null,
            clientId,
            appId,
            currentEnvironment,
          }),
          'Client Authentication is unavailable.',
        ),
        connectionRef.current.GetMetadata({ forceReload: true }),
        mappingRef.current.GetMetadata({ forceReload: true }),
        azureMapsRef.current.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(loaded);
      setConnectionMetadata(
        getFirstMetadata(connectionResult, connectionRef.current),
      );
      setMappingMetadata(
        getFirstMetadata(mappingResult, mappingRef.current),
      );
      setAzureMapsMetadata(
        getFirstMetadata(azureMapsResult, azureMapsRef.current),
      );
      return loaded;
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client Authentication is unavailable.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const mappings = workspace?.Mappings || [];
    setSelectedMappingId((previous) => {
      if (mappings.some((mapping) => String(mapping.Id) === String(previous))) {
        return previous;
      }
      return workspace?.DefaultMappingId
        || mappings.find((mapping) => mapping.IsActive)?.Id
        || mappings[0]?.Id
        || '';
    });
  }, [workspace]);

  const execute = async (
    operationName,
    operationRequest,
    successMessage,
    resultTitle,
    close = null,
    resultSurface = 'dialog',
  ) => {
    setBusy(true);
    setOperationResult(null);
    try {
      const updated = await runOperation(
        workspaceRef.current,
        operationName,
        operationRequest,
        successMessage,
      );
      setWorkspace(updated);
      if (close) close();
      setOperationResult({
        title: `${resultTitle} Result`,
        message: updated?.StatusMessage || successMessage,
        tone: 'success',
      });
      return updated;
    } catch (ex) {
      if (resultSurface === 'dialog') {
        setOperationResult({
          title: `${resultTitle} Result`,
          message: ex?.message || successMessage,
          tone: 'error',
        });
      }
      throw ex;
    } finally {
      setBusy(false);
    }
  };

  const mappingFields = useMemo(
    () => selectSecurityFields(
      mappingMetadata,
      CLIENT_ENTRA_MAPPING_FIELDS,
    ),
    [mappingMetadata],
  );
  const connectionFields = useMemo(
    () => selectSecurityFields(
      connectionMetadata,
      CLIENT_ENTRA_PROVIDER_SETUP_FIELDS,
    ).map((field) => {
      const fieldName = field?.FieldName || field?.fieldName;
      const presentation =
        ENTRA_PROVIDER_SETUP_FIELD_PRESENTATION[fieldName];
      return {
        ...field,
        IsRequired: fieldName === 'DisplayName',
        isRequired: fieldName === 'DisplayName',
        ...(presentation ? {
          Description: presentation.Description,
          description: presentation.Description,
        } : {}),
      };
    }),
    [connectionMetadata],
  );
  const azureMapsFields = useMemo(
    () => selectSecurityFields(
      azureMapsMetadata,
      CLIENT_AZURE_MAPS_CONNECTION_FIELDS,
    ),
    [azureMapsMetadata],
  );
  const mappingCardMetadata = useMutableSecurityGridMetadata(
    AUTH_MAPPING_CARD_METADATA,
  );
  const canSaveEntraConnection = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientEntraReadConnection,
  );
  const canSaveAzureMapsConnection = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientAzureMapsConnection,
  );
  const canRemoveAzureMapsOverride = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.removeClientAzureMapsOverride,
  );
  const canValidateEntraConnection = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.validateClientEntraReadConnection,
  );
  const canTestEntraLogin = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.testClientEntraLogin,
  );
  const canSaveEntraMapping = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientEntraMapping,
  );
  const canDeleteEntraMapping = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.deleteClientEntraMapping,
  );
  const canLoadGuestAccess = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.loadClientGuestAccess,
  );
  const canSaveGuestAccessMode = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientGuestAccessMode,
  );
  const canGenerateEntraSetupScript = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.generateClientEntraSetupScript,
  );
  const selectedMapping = useMemo(
    () => (workspace?.Mappings || []).find(
      (mapping) => String(mapping.Id) === String(selectedMappingId),
    ) || null,
    [selectedMappingId, workspace],
  );
  const selectedMappingIsEntra = Boolean(
    selectedMapping
    && (!selectedMapping.ProviderKey
      || selectedMapping.ProviderKey === 'EntraID'),
  );
  const selectedMappingIsGuest = selectedMapping?.ProviderKey === 'Guest';
  const selectedMappingOwnsReadConnection = Boolean(
    selectedMappingIsEntra
    && selectedMapping?.IsActive
    && String(selectedMapping?.TenantId || '').toLowerCase()
      === String(workspace?.ReadLogin?.TenantId || '').toLowerCase()
    && String(selectedMapping?.AuthProviderClientId || '').toLowerCase()
      === String(workspace?.ReadLogin?.SignInClientId || '').toLowerCase(),
  );
  const selectedConnectionStatus = !selectedMapping
    ? ''
    : selectedMappingIsGuest
      ? (selectedMapping.IsActive ? 'Active' : 'Disabled')
      : selectedMapping.Status === 'Pending setup'
      ? 'Pending setup'
      : !selectedMapping.IsActive
      ? 'Disabled'
      : selectedMappingOwnsReadConnection
        ? workspace?.ReadLogin?.Status
        : 'Setup required';
  const providerDraftOwnsConnection = Boolean(
    connectionOpen
    && providerSetupIssued
    && String(connectionDraft?.TenantId || '').toLowerCase()
      === String(workspace?.ReadLogin?.TenantId || '').toLowerCase()
    && String(connectionDraft?.SignInClientId || '').toLowerCase()
      === String(workspace?.ReadLogin?.SignInClientId || '').toLowerCase(),
  );
  const providerSetupComplete = Boolean(
    providerDraftOwnsConnection
    && workspace?.ReadLogin?.ValidationStatus === 'Validated',
  );
  const providerSetupStatusMessage = providerSetupComplete
    ? 'Completed and validated'
    : providerDraftOwnsConnection
      ? workspace?.ReadLogin?.ValidationStatus
      : providerSetupIssued
        ? 'Waiting for Microsoft Entra setup'
        : 'Download a setup script when the client administrator is ready.';
  const entraSetupEmail = useMemo(
    () => buildClientEntraSetupEmail({
      currentEnvironment,
      run: {
        Name: workspace?.ClientName,
        ShortName: workspace?.ClientShortName,
        CurrentEnvironment:
          workspace?.DisplayEnvironment || currentEnvironment,
        EntraTenantId: connectionOpen
          ? connectionDraft?.TenantId
          : workspace?.ReadLogin?.TenantId,
        EntraTenantDomain: connectionOpen
          ? connectionDraft?.TenantDomain
          : workspace?.ReadLogin?.TenantDomain,
      },
    }),
    [
      connectionDraft?.TenantDomain,
      connectionDraft?.TenantId,
      connectionOpen,
      currentEnvironment,
      workspace,
    ],
  );

  const openAzureMaps = () => {
    setAzureMapsDraft(
      cloneSecurityValue(workspace?.AzureMapsForm || {}),
    );
    setAzureMapsOpen(true);
  };

  const saveAzureMaps = async () => execute(
    CLIENT_SECURITY_OPERATIONS.saveAzureMapsConnection,
    request({ AzureMapsForm: azureMapsDraft }),
    'Client Azure Maps override saved.',
    'Override Azure Maps',
    () => setAzureMapsOpen(false),
  );

  const removeAzureMapsOverride = async () => execute(
    CLIENT_SECURITY_OPERATIONS.removeAzureMapsOverride,
    request({
      ConfirmRemoveAzureMapsOverride: true,
      AzureMapsForm: workspace?.AzureMapsForm,
    }),
    'Client Azure Maps override removed.',
    'Remove Client Azure Maps Override',
    () => setRemoveAzureMapsOpen(false),
    'origin',
  );

  const validateConnection = async () => execute(
    CLIENT_SECURITY_OPERATIONS.validateReadConnection,
    request(),
    'Read connection validation completed.',
    'Validate Read Connection',
  );

  const generateSetupScript = async () => {
    setBusy(true);
    setOperationResult(null);
    try {
      const scriptItem = await runOperation(
        setupScriptRef.current,
        CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
        {
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          ProviderMappingId: selectedMappingId,
        },
        'The Microsoft Entra setup script could not be generated.',
      );
      downloadClientEntraSetupScript(scriptItem);
      setInstructionsOpen(false);
      setOperationResult({
        title: 'Download Microsoft Entra Setup Script Result',
        message: `Downloaded ${scriptItem.FileName}. Send it only to an authorized administrator for the client's Microsoft Entra tenant.`,
        tone: 'success',
      });
      return { Success: true, Items: [scriptItem] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message
          || 'The Microsoft Entra setup script could not be generated.',
      };
    } finally {
      setBusy(false);
    }
  };

  const openClientSetupEmail = () => {
    setInstructionsOpen(false);
    setEntraEmailOpen(true);
  };

  const copyClientSetupEmail = async () => {
    setOperationResult(null);
    try {
      if (!globalThis.navigator?.clipboard?.writeText) {
        throw new Error('Clipboard access is unavailable.');
      }
      await globalThis.navigator.clipboard.writeText(
        entraSetupEmail.clipboardText,
      );
      setEntraEmailOpen(false);
      setOperationResult({
        title: 'Copy Client Email Result',
        message: 'The client Microsoft Entra setup email was copied.',
        tone: 'success',
      });
      return { Success: true };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message
          || 'The client Microsoft Entra setup email could not be copied.',
      };
    }
  };

  const generateResetProtectedConnectionScript = async () => {
    setBusy(true);
    setOperationResult(null);
    try {
      const scriptItem = await runOperation(
        setupScriptRef.current,
        CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
        {
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          ProviderMappingId: selectedMappingId,
        },
        'The Microsoft Entra reset script could not be generated.',
      );
      downloadClientEntraSetupScript(scriptItem);
      setResetProtectedConnectionOpen(false);
      setOperationResult({
        title: 'Reset Protected Connection Result',
        message: `Downloaded ${scriptItem.FileName}. Run it with -ResetProtectedConnection to rotate only the protected credential.`,
        tone: 'success',
      });
      return { Success: true, Items: [scriptItem] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message
          || 'The Microsoft Entra reset script could not be generated.',
      };
    } finally {
      setBusy(false);
    }
  };

  const testLogin = async () => {
    const readLogin = workspace?.ReadLogin;
    if (!readLogin?.TenantId || !readLogin?.SignInClientId) {
      setOperationResult({
        title: 'Test Login Result',
        message: 'Configure the Entra Read / Login connection before testing login.',
        tone: 'error',
      });
      return;
    }
    setBusy(true);
    setOperationResult(null);
    try {
      const msal = new PublicClientApplication({
        auth: {
          clientId: readLogin.SignInClientId,
          authority: `https://login.microsoftonline.com/${readLogin.TenantId}`,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      });
      await msal.initialize();
      const login = await msal.loginPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'select_account',
      });
      const token = login?.idToken;
      if (!token) throw new Error('Microsoft Entra did not return an ID token.');
      const updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.testLogin,
        request({ LoginToken: token }),
        'The Entra login test failed.',
      );
      setWorkspace(updated);
      setOperationResult({
        title: 'Test Login Result',
        message: updated?.StatusMessage || 'The Entra login test succeeded.',
        tone: 'success',
      });
    } catch (ex) {
      if (isUserCancelledLogin(ex)) {
        setOperationResult({
          title: 'Test Login Result',
          subTitle: 'The login test was cancelled.',
          message: 'No changes were made.',
          tone: 'neutral',
        });
        return;
      }
      setOperationResult({
        title: 'Test Login Result',
        message: ex?.message || 'The Entra login test failed.',
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const applyGuestWorkspace = (updated) => {
    setGuestWorkspace(updated);
    setGuestDraft({
      ...cloneSecurityValue(updated || {}),
      GuestAccessActive: updated?.GuestAccessActive === true,
      ReadinessStatus: updated?.ReadyForActivation === true
        ? 'Ready'
        : 'Setup required',
      CanonicalOrigin: updated?.CanonicalOrigin || window.location.origin,
      RedirectUri: updated?.RedirectUri
        || `${window.location.origin}/microsoft/sso`,
    });
  };

  const loadGuestAccess = async () => {
    setBusy(true);
    setOperationResult(null);
    setGuestResult(null);
    try {
      const loaded = await runOperation(
        guestRef.current,
        CLIENT_SECURITY_OPERATIONS.loadGuestAccess,
        createSecurityRequest({
          workspace: null,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Guest / External Access is unavailable.',
      );
      applyGuestWorkspace(loaded);
      setProviderSelectionOpen(false);
      setGuestOpen(true);
      return loaded;
    } catch (ex) {
      setOperationResult({
        title: 'Guest / External Access Result',
        message: ex?.message || 'Guest / External Access is unavailable.',
        tone: 'error',
      });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const executeGuest = async (
    operationName,
    overrides,
    successMessage,
  ) => {
    setBusy(true);
    setGuestResult(null);
    try {
      const updated = await runOperation(
        guestRef.current,
        operationName,
        createSecurityRequest({
          workspace: guestWorkspace,
          clientId,
          appId,
          currentEnvironment,
          overrides,
        }),
        successMessage,
      );
      applyGuestWorkspace(updated);
      setGuestResult({
        message: updated?.StatusMessage || successMessage,
        tone: 'success',
      });
      return updated;
    } catch (ex) {
      setGuestResult({
        message: ex?.message || successMessage,
        tone: 'error',
      });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveGuestMode = async () => {
    const updated = await executeGuest(
      CLIENT_SECURITY_OPERATIONS.saveGuestAccessMode,
      {
        GuestAccessActive: guestDraft?.GuestAccessActive === true,
        CanonicalOrigin: guestDraft?.CanonicalOrigin || '',
        RedirectUri: guestDraft?.RedirectUri || '',
      },
      'Guest authentication mode could not be saved.',
    );
    if (updated) {
      setGuestOpen(false);
      await refresh();
    }
  };

  const openMapping = (mapping) => {
    if (!mapping) return;
    setMappingDraft({
      ...(workspace?.MappingForm || {}),
      ...cloneSecurityValue(mapping),
      MappingId: mapping.Id,
      Enabled: mapping.IsActive,
      ExpectedVersion: mapping.ConcurrencyToken,
    });
    setMappingOpen(true);
  };

  const openProviderSelection = () => {
    setSelectedProviderKeys([]);
    setProviderSelectionOpen(true);
  };

  const openPendingProviderSetup = (
    mapping,
    updatedWorkspace = workspace,
  ) => {
    if (!mapping?.Id) return;
    setWorkspace(updatedWorkspace);
    setSelectedMappingId(mapping.Id);
    setProviderSelectionOpen(false);
    setConnectionDraft({
      ...(cloneSecurityValue(updatedWorkspace?.ConnectionForm || {})),
      ProviderMappingId: mapping.Id,
      TenantId: '',
      TenantDomain: '',
      SignInClientId: '',
      ReadClientId: '',
      ReadClientSecretInput: '',
      ReadCredentialConfigured: false,
      DisplayName: mapping.DisplayName || 'Microsoft Entra ID',
      Icon: mapping.Icon || '',
    });
    setProviderSetupIssued(false);
    setProviderSetupBaselineValidationAt(
      updatedWorkspace?.ReadLogin?.LastValidatedAtUtc || '',
    );
    setConnectionOpen(true);
  };

  const continueProviderSelection = async () => {
    if (selectedProviderKeys.includes('Guest')) {
      return loadGuestAccess();
    }
    if (!selectedProviderKeys.includes('EntraID')) return undefined;
    setBusy(true);
    setOperationResult(null);
    try {
      const updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.saveEntraMapping,
        request({
          ConfirmCreateProviderMapping: true,
          MappingForm: {
            ...(cloneSecurityValue(workspace?.MappingForm || {})),
            MappingId: '',
            TenantId: '',
            AuthProviderClientId: '',
            Enabled: false,
          },
        }),
        'The pending Microsoft Entra provider mapping could not be created.',
      );
      const pendingMapping = (updated?.Mappings || []).find(
        (mapping) => mapping.Status === 'Pending setup',
      );
      if (!pendingMapping?.Id) {
        throw new Error(
          'The pending Microsoft Entra provider mapping is unavailable.',
        );
      }
      openPendingProviderSetup(pendingMapping, updated);
      return { Success: true, Items: [updated] };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage: ex?.message
          || 'The pending Microsoft Entra provider mapping could not be created.',
      };
    } finally {
      setBusy(false);
    }
  };

  const closeProviderSetup = () => {
    setConnectionOpen(false);
    setProviderSetupIssued(false);
    setProviderSetupBaselineValidationAt('');
  };

  const saveProviderConnection = async (
    providerDraft = connectionDraft,
  ) => {
    const updated = await execute(
      CLIENT_SECURITY_OPERATIONS.saveReadConnection,
      request({
        ConnectionForm: providerDraft,
        ConfirmCreateProviderMapping: true,
        MappingId: providerDraft?.ProviderMappingId,
      }),
      'Microsoft Entra ID provider added. Validate the read connection next.',
      'Add Microsoft Entra ID',
      closeProviderSetup,
    );
    setSelectedMappingId(updated?.DefaultMappingId || '');
    return updated;
  };

  const openProviderSetupEmail = (providerDraft) => {
    setConnectionDraft(cloneSecurityValue(providerDraft));
    setEntraEmailOpen(true);
  };

  const generateProviderSetupScript = async (providerDraft) => {
    setBusy(true);
    setOperationResult(null);
    try {
      setConnectionDraft(cloneSecurityValue(providerDraft));
      const scriptItem = await runOperation(
        setupScriptRef.current,
        CLIENT_ENTRA_SETUP_SCRIPT_OPERATION,
        {
          ClientId: clientId,
          ApplicationId: appId,
          CurrentEnvironment: currentEnvironment,
          AuthenticationProvider: 'Microsoft Entra ID',
          ConfirmCreateProviderMapping: true,
          ProviderMappingId: providerDraft?.ProviderMappingId,
          RequestedDisplayName:
            providerDraft?.DisplayName || 'Microsoft Entra ID',
          RequestedTenantId: providerDraft?.TenantId,
          RequestedTenantDomain:
            providerDraft?.TenantDomain,
          RequestedSignInClientId:
            providerDraft?.SignInClientId,
          RequestedReadClientId:
            providerDraft?.ReadClientId,
        },
        'The Microsoft Entra setup script could not be generated.',
      );
      downloadClientEntraSetupScript(scriptItem);
      setProviderSetupIssued(true);
      const providerMappingId = scriptItem?.ProviderMappingId || '';
      await refresh();
      setSelectedMappingId(providerMappingId);
      setOperationResult({
        title: 'Entra Setup Result',
        message: `Downloaded ${scriptItem.FileName}. Use the refresh icon after the client Microsoft Entra administrator completes it.`,
        tone: 'success',
      });
    } catch (ex) {
      setOperationResult({
        title: 'Entra Setup Result',
        message: ex?.message
          || 'The Microsoft Entra setup script could not be generated.',
        tone: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const refreshProviderSetupStatus = async () => {
    const updated = await refresh();
    if (!updated) return;
    const validationTime = Date.parse(
      updated?.ReadLogin?.LastValidatedAtUtc || '',
    );
    const baselineValidationTime = Date.parse(
      providerSetupBaselineValidationAt,
    );
    const matchesIssuedProvider = (
      String(connectionDraft?.TenantId || '').toLowerCase()
        === String(updated?.ReadLogin?.TenantId || '').toLowerCase()
      && String(connectionDraft?.SignInClientId || '').toLowerCase()
        === String(updated?.ReadLogin?.SignInClientId || '').toLowerCase()
    );
    const completedAfterScriptIssued = (
      providerSetupIssued
      && Number.isFinite(validationTime)
      && (
        !Number.isFinite(baselineValidationTime)
        || validationTime > baselineValidationTime
      )
    );
    if (!matchesIssuedProvider && !completedAfterScriptIssued) return;
    setConnectionDraft({
      ...cloneSecurityValue(updated.ConnectionForm || {}),
      ProviderMappingId:
        connectionDraft?.ProviderMappingId,
      ReadClientSecretInput: '',
    });
    setSelectedMappingId(updated.DefaultMappingId || '');
  };

  const saveMapping = async () => {
    const updated = await execute(
      CLIENT_SECURITY_OPERATIONS.saveEntraMapping,
      request({ MappingForm: mappingDraft }),
      'Entra provider mapping saved.',
      'Edit Provider Mapping',
      () => setMappingOpen(false),
    );
    const savedMapping = (updated?.Mappings || []).find((mapping) => (
      String(mapping.Id) === String(mappingDraft?.MappingId)
    ));
    setSelectedMappingId(savedMapping?.Id || updated?.DefaultMappingId || '');
    return updated;
  };

  const changeMappingStatus = async () => {
    const mappingId = pendingMappingStatus?.Id || '';
    const updated = await execute(
      CLIENT_SECURITY_OPERATIONS.saveEntraMapping,
      request({
        MappingForm: {
          ...(workspace?.MappingForm || {}),
          ...pendingMappingStatus,
          MappingId: mappingId,
          Enabled: !pendingMappingStatus.IsActive,
          ExpectedVersion: pendingMappingStatus.ConcurrencyToken,
        },
      }),
      'Entra provider mapping status changed.',
      pendingMappingStatus?.IsActive
        ? 'Disable Entra Mapping'
        : 'Enable Entra Mapping',
      () => setPendingMappingStatus(null),
      'origin',
    );
    setSelectedMappingId(mappingId || updated?.DefaultMappingId || '');
    return updated;
  };

  const deleteMapping = async () => {
    const mappingId = pendingDeleteMapping?.Id || '';
    const updated = await execute(
      CLIENT_SECURITY_OPERATIONS.deleteEntraMapping,
      request({
        MappingId: mappingId,
        ConfirmDeleteProviderMapping: true,
        MappingForm: {
          ...(workspace?.MappingForm || {}),
          MappingId: mappingId,
          ExpectedVersion:
            pendingDeleteMapping?.ConcurrencyToken || '',
        },
      }),
      'Entra provider mapping deleted.',
      'Delete Provider Mapping',
      () => setPendingDeleteMapping(null),
      'origin',
    );
    setSelectedMappingId(
      updated?.DefaultMappingId
        || updated?.Mappings?.[0]?.Id
        || '',
    );
    return updated;
  };

  const authenticationActions = [
    ...(selectedMappingIsGuest && canLoadGuestAccess ? [{
      key: 'manage-guest-access',
      label: 'Manage Guest Access',
      iconKey: 'loginscreen',
      disabled: loading || busy || !workspace,
      onClick: loadGuestAccess,
    }] : []),
    ...(canSaveEntraMapping && selectedMappingIsEntra
      && selectedMapping?.Status !== 'Pending setup' ? [{
      key: 'manage-entra-provider',
      label: 'Manage Microsoft Entra ID',
      iconKey: 'edit',
      disabled: loading || busy || !workspace || !mappingMetadata,
      onClick: () => openMapping(selectedMapping),
    }] : []),
    ...(canSaveEntraMapping && selectedMappingIsEntra
      && selectedMapping?.Status === 'Pending setup' ? [{
      key: 'complete-entra-setup',
      label: 'Complete Entra Setup',
      iconKey: 'edit',
      disabled: loading || busy || !workspace,
      onClick: () => openPendingProviderSetup(selectedMapping),
    }] : []),
    ...(canGenerateEntraSetupScript
      && selectedMappingIsEntra && selectedMapping?.IsActive ? [{
      key: 'configure-entra-tenant',
      label: 'Configure Entra Tenant',
      iconKey: 'download',
      disabled: loading || busy || !workspace,
      onClick: () => setInstructionsOpen(true),
    }] : []),
    ...(selectedMappingOwnsReadConnection ? [
      ...(canValidateEntraConnection ? [
      {
        key: 'validate-read-connection',
        label: 'Validate Read Connection',
        iconKey: 'validate',
        disabled: loading || busy || !workspace,
        onClick: validateConnection,
      },
      ] : []),
      ...(canGenerateEntraSetupScript ? [
      {
        key: 'reset-protected-connection',
        label: 'Reset Protected Connection',
        iconKey: 'undo',
        disabled: loading || busy || !workspace,
        onClick: () => setResetProtectedConnectionOpen(true),
      },
      ] : []),
      ...(canTestEntraLogin ? [
      {
        key: 'test-client-login',
        label: 'Test Login',
        iconKey: 'loginscreen',
        disabled: loading
          || busy
          || workspace?.ReadLogin?.ValidationStatus !== 'Validated',
        onClick: testLogin,
      },
      ] : []),
    ] : []),
    ...(canSaveAzureMapsConnection ? [
      {
        key: 'override-azure-maps',
        label: 'Override Azure Maps',
        iconKey: 'settings',
        disabled: loading || busy || !workspace || !azureMapsMetadata,
        onClick: openAzureMaps,
      },
    ] : []),
    ...(canRemoveAzureMapsOverride
      && workspace?.AzureMaps?.HasClientOverride ? [{
        key: 'remove-azure-maps-override',
        label: 'Remove Client Override',
        iconKey: 'delete',
        disabled: loading || busy,
        onClick: () => setRemoveAzureMapsOpen(true),
      }] : []),
  ];
  const mappingHeaderActions = [
    ...(canSaveEntraMapping ? [
    {
      Key: 'complete-provider-setup',
      Title: 'Complete setup',
      IconKey: 'edit',
      ActionCallback: (mapping) => openPendingProviderSetup(mapping),
      Visible: (mapping) => mapping?.ProviderKey !== 'Guest'
        && mapping?.Status === 'Pending setup',
      Disabled: busy || !connectionMetadata,
    },
    ] : []),
    ...(canDeleteEntraMapping ? [
    {
      Key: 'delete-provider-mapping',
      Title: 'Delete',
      IconKey: 'delete',
      Tone: 'danger',
      ActionCallback: setPendingDeleteMapping,
      Visible: (mapping) => mapping?.ProviderKey !== 'Guest',
      Disabled: (mapping) => busy || mapping?.IsActive === true,
    },
    ] : []),
  ];

  return (
    <div className="resource-workspace-overview command-entry command-security">
      <SecurityToolbar
        busy={loading || busy}
        onRefresh={refresh}
        actions={authenticationActions}
        ariaLabel="Authentication actions"
        spacious
      />
      <SecurityState
        loading={loading}
        error={error}
        label="Client Authentication"
      />
      {!loading && workspace ? (
        <>
          <div className="command-security__provider-workspace">
            <section className="resource-workspace-panel command-security__provider-list">
              <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                <div>
                  <h2>Provider Mappings</h2>
                  <p>Select a provider to view its connection information.</p>
                </div>
                {canSaveEntraMapping ? (
                  <div className="resource-workspace-section-actions">
                    <ButtonControl
                      type="icon"
                      className="command-security__add-provider"
                      title="Add Authentication Provider"
                      aria-label="Add Authentication Provider"
                      disabled={loading || busy || !connectionMetadata}
                      onClick={openProviderSelection}
                    >
                      <IconControl
                        iconKey="add"
                        title="Add Authentication Provider"
                        width="1rem"
                        height="1rem"
                      />
                    </ButtonControl>
                  </div>
                ) : null}
              </div>
              <CardGridControl
                className="command-security__mapping-card-grid"
                rows={workspace.Mappings || []}
                metadata={mappingCardMetadata}
                cardFields={mappingCardMetadata[0].Fields}
                rowKey={(mapping) => mapping.Id}
                selectable
                selectedRowKeys={selectedMappingId ? [selectedMappingId] : []}
                onSelectedRowKeysChange={(keys, mapping) => (
                  setSelectedMappingId(keys?.[0] || mapping?.Id || '')
                )}
                headerIconActions={mappingHeaderActions}
                selectedHeaderIconActions={mappingHeaderActions}
                readOnly={false}
                disabled={busy}
                disableInlineEdit
                disableCardClickEdit
                toggleEnabled={canSaveEntraMapping}
                toggleFieldName="IsActive"
                showToggleWhenSelected
                toggleActiveLabel="Disable"
                toggleInactiveLabel="Enable"
                onToggleChange={(_updated, _nextValue, mapping) => {
                  if (mapping?.ProviderKey === 'Guest') {
                    loadGuestAccess();
                    return;
                  }
                  if (mapping?.Status === 'Pending setup') {
                    openPendingProviderSetup(mapping);
                    return;
                  }
                  setPendingMappingStatus(mapping);
                }}
                activeFilterEnabled={false}
                emptyContent="No authentication provider mappings are configured."
                minCardWidth="16rem"
                maxColumns={1}
              />
            </section>

            <div className="command-security__provider-detail">
              {selectedMapping ? (
                <StatusSection
                  title="Connection information"
                  mode={selectedMappingIsGuest
                    ? 'Manage readiness and activation with Manage Guest Access. Invitations are managed from User Profiles.'
                    : !selectedMapping.IsActive
                      ? 'Enable this provider mapping to use its connection actions.'
                      : !selectedMappingOwnsReadConnection
                        ? 'Configure this Entra tenant before using its connection actions.'
                        : selectedMapping.DisplayName}
                  status={selectedConnectionStatus}
                >
                  <StatusValue
                    label="Authentication provider"
                    value={selectedMappingIsGuest
                      ? 'Guest / External Access'
                      : 'Microsoft Entra ID'}
                  />
                  <StatusValue
                    label="Tenant ID"
                    value={selectedMapping.TenantId}
                  />
                  <StatusValue
                    label="Sign-in Application Client ID"
                    value={selectedMapping.AuthProviderClientId}
                  />
                  <StatusValue
                    label="Scope / Audience URI"
                    value={selectedMapping.ScopeIdUri}
                  />
                  {selectedMappingOwnsReadConnection ? (
                    <>
                      <StatusValue
                        label="Graph Read Application Client ID"
                        value={workspace.ReadLogin?.ReadClientId}
                      />
                      <StatusValue
                        label="Directory read credential"
                        value={workspace.ReadLogin?.ReadCredentialConfigured
                          ? 'Configured'
                          : 'Not configured'}
                      />
                      <StatusValue
                        label="Issuer"
                        value={workspace.ReadLogin?.EffectiveIssuer}
                      />
                      <StatusValue
                        label="Last validation"
                        value={formatLocalDateTime(
                          workspace.ReadLogin?.LastValidatedAtUtc,
                        )}
                      />
                      <StatusValue
                        label="Last successful login test"
                        value={formatLocalDateTime(
                          workspace.ReadLogin?.LastSuccessfulLoginTestAtUtc,
                          'Not tested',
                        )}
                      />
                      <StatusValue
                        label="Directory management"
                        value={workspace.Management?.Status}
                      />
                      <ConvokeDialogMessage
                        message={workspace.ReadLogin?.Blocker}
                        tone="error"
                      />
                    </>
                  ) : null}
                </StatusSection>
              ) : (
                <ResourceStateControl
                  compact
                  title="No provider selected"
                  message="Add or select an authentication provider mapping."
                />
              )}

              {workspace.AzureMaps?.HasClientOverride ? (
                <StatusSection
                  title="Azure Maps address search"
                  mode={workspace.AzureMaps?.Source}
                  status={workspace.AzureMaps?.Status}
                >
                  <StatusValue
                    label="Authentication mode"
                    value={workspace.AzureMaps?.AuthenticationMode}
                  />
                  <StatusValue
                    label="Azure Maps account client ID"
                    value={workspace.AzureMaps?.AccountClientId}
                  />
                  <StatusValue
                    label="Tenant ID"
                    value={workspace.AzureMaps?.TenantId}
                  />
                  <StatusValue
                    label="Application client ID"
                    value={workspace.AzureMaps?.ApplicationClientId}
                  />
                  <StatusValue
                    label="Client credential"
                    value={workspace.AzureMaps?.AzureMapsClientCredentialConfigured
                      ? 'Configured'
                      : 'Not configured'}
                  />
                </StatusSection>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      <SidePanelFormControl
        open={azureMapsOpen}
        title="Override Azure Maps"
        metadata={azureMapsMetadata}
        formFields={azureMapsFields}
        itemData={azureMapsDraft}
        onSave={saveAzureMaps}
        onCancel={() => setAzureMapsOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Save"
        setOpenEditSidePanel={setAzureMapsOpen}
        setItemData={setAzureMapsDraft}
        viewOnly={!canSaveAzureMapsConnection}
      />
      <ClientEntraProviderSetupPanel
        open={connectionOpen}
        metadata={connectionMetadata}
        formFields={connectionFields}
        itemData={connectionDraft}
        canWrite={canSaveEntraConnection}
        busy={busy}
        providerSetupIssued={providerSetupIssued}
        setupComplete={providerSetupComplete}
        statusMessage={providerSetupStatusMessage}
        onSave={saveProviderConnection}
        onCancel={closeProviderSetup}
        onClientEmail={openProviderSetupEmail}
        onEntraSetup={generateProviderSetupScript}
        onRefresh={refreshProviderSetupStatus}
      />
      <SidePanelFormControl
        open={mappingOpen}
        title="Edit Provider Mapping"
        metadata={mappingMetadata}
        formFields={mappingFields}
        itemData={mappingDraft}
        onSave={saveMapping}
        onCancel={() => setMappingOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Save"
        setOpenEditSidePanel={setMappingOpen}
        setItemData={setMappingDraft}
        viewOnly={!canSaveEntraMapping}
      />
      <DialogControl
        openDialog={!!pendingDeleteMapping}
        title="Delete Provider Mapping"
        subTitle="Only inactive provider mappings can be deleted."
        okText="Delete"
        cancelText="Cancel"
        onCancel={() => setPendingDeleteMapping(null)}
        onX={() => setPendingDeleteMapping(null)}
        onOk={deleteMapping}
        disableOk={busy || pendingDeleteMapping?.IsActive === true}
        loadingOk={busy}
      >
        <p>
          Delete {pendingDeleteMapping?.DisplayName
            || 'this provider mapping'}?
        </p>
      </DialogControl>
      <DialogControl
        openDialog={providerSelectionOpen}
        title="Add Authentication Provider"
        subTitle="Choose an authentication provider for this client application."
        okText="Continue"
        cancelText="Cancel"
        onCancel={() => setProviderSelectionOpen(false)}
        onX={() => setProviderSelectionOpen(false)}
        onOk={continueProviderSelection}
        disableOk={!selectedProviderKeys.some((key) => (
          key === 'EntraID' || key === 'Guest'
        ))}
        className="command-entry__options-dialog"
      >
        <CardGridControl
          className="command-entry__options-card-grid"
          rows={AUTH_PROVIDER_SELECTION_OPTIONS}
          metadata={AUTH_PROVIDER_SELECTION_CARD_METADATA}
          cardFields={AUTH_PROVIDER_SELECTION_CARD_METADATA.Fields}
          rowKey={(provider) => provider.Key}
          selectedRowKeys={selectedProviderKeys}
          onSelectedRowKeysChange={setSelectedProviderKeys}
          selectable
          activeFilterEnabled={false}
          selectableRowDisabledResolver={(provider) => (
            provider.IsSelectable === false
          )}
          showSelectionLockIcon={false}
          emptyContent="No authentication providers are available."
          minCardWidth="16rem"
          maxColumns={2}
        />
      </DialogControl>
      <ClientGuestAccessPanel
        open={guestOpen}
        workspace={guestWorkspace}
        result={guestResult}
        draft={guestDraft}
        setDraft={setGuestDraft}
        canSave={canSaveGuestAccessMode}
        busy={busy}
        onClose={() => setGuestOpen(false)}
        onOpenDomains={() => {
          navigate(`/command/clients/${clientId}/domains`);
        }}
        onSaveMode={saveGuestMode}
      />
      <DialogControl
        openDialog={resetProtectedConnectionOpen}
        title="Reset Protected Microsoft Entra Connection"
        subTitle="The application client IDs are preserved. The downloaded one-time script rotates only the protected Graph Read credential."
        okText="Download Reset Script"
        cancelText="Cancel"
        onCancel={() => setResetProtectedConnectionOpen(false)}
        onX={() => setResetProtectedConnectionOpen(false)}
        onOk={generateResetProtectedConnectionScript}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Download a new one-time script, then run it with
          {' '}
          <code>-ResetProtectedConnection</code>
          . Convoke validates the replacement before the script removes
          superseded Convoke-managed credentials.
        </p>
      </DialogControl>
      <DialogControl
        openDialog={removeAzureMapsOpen}
        title="Remove Client Azure Maps Override"
        subTitle="The client will immediately resume the application-level Azure Maps configuration."
        okText="Remove Override"
        cancelText="Cancel"
        onCancel={() => setRemoveAzureMapsOpen(false)}
        onX={() => setRemoveAzureMapsOpen(false)}
        onOk={removeAzureMapsOverride}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          Remove the client-specific Azure Maps configuration and its protected
          credential?
        </p>
      </DialogControl>
      <DialogControl
        openDialog={instructionsOpen}
        title="Configure Entra Tenant"
        subTitle="Using the same guided client handoff available during onboarding."
        okText="Download Setup Script"
        cancelText="Close"
        onCancel={() => setInstructionsOpen(false)}
        onX={() => setInstructionsOpen(false)}
        onOk={generateSetupScript}
        disableOk={busy || !canGenerateEntraSetupScript || !setupScriptManager}
        loadingOk={busy}
      >
        <ol className="command-security__instructions">
          <li>
            Select <strong>Download Setup Script</strong> to create the
            client-specific setup file.
          </li>
          <li>
            Send the script only to an authorized administrator for the
            client&apos;s Microsoft Entra tenant.
          </li>
          <li>
            After the administrator confirms completion, select
            {' '}
            <strong>Refresh</strong>
            {' '}
            and then
            {' '}
            <strong>Validate Read Connection</strong>.
          </li>
          <li>
            <a
              href="#client-entra-setup-email"
              className="command-security__link"
              onClick={(event) => {
                event.preventDefault();
                openClientSetupEmail();
              }}
            >
              Click here to review and copy the client email
            </a>
            , then attach the downloaded setup script before sending it.
          </li>
        </ol>
      </DialogControl>
      <DialogControl
        openDialog={entraEmailOpen}
        title="Client Microsoft Entra setup email"
        subTitle="Review the client-ready message, then copy it into an approved secure email."
        okText="Copy Email"
        cancelText="Close"
        onCancel={() => setEntraEmailOpen(false)}
        onX={() => setEntraEmailOpen(false)}
        onOk={copyClientSetupEmail}
      >
        <div className="command-onboarding__entra-email">
          <section>
            <strong>Subject</strong>
            <div>{entraSetupEmail.subject}</div>
          </section>
          <section>
            <strong>Body</strong>
            <pre>{entraSetupEmail.body}</pre>
          </section>
        </div>
      </DialogControl>
      <DialogControl
        openDialog={!!pendingMappingStatus}
        title={`${pendingMappingStatus?.IsActive ? 'Disable' : 'Enable'} Entra Mapping`}
        subTitle="This changes only the selected local provider mapping."
        okText="Confirm"
        cancelText="Cancel"
        onCancel={() => setPendingMappingStatus(null)}
        onX={() => setPendingMappingStatus(null)}
        onOk={changeMappingStatus}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          {pendingMappingStatus?.IsActive ? 'Disable' : 'Enable'}
          {' '}
          {pendingMappingStatus?.DisplayName}?
        </p>
      </DialogControl>
      <DialogControl
        openDialog={!!operationResult}
        title={operationResult?.title || 'Authentication Result'}
        subTitle={operationResult?.subTitle || (
          operationResult?.tone === 'error'
            ? 'The operation did not complete.'
            : 'The operation completed.'
        )}
        okText="Close"
        cancelText=""
        onCancel={() => setOperationResult(null)}
        onX={() => setOperationResult(null)}
        onOk={() => setOperationResult(null)}
      >
        <ConvokeDialogMessage
          message={operationResult?.message}
          tone={operationResult?.tone}
        />
      </DialogControl>
    </div>
  );
};

export const ClientRolesPermissionsScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const workspaceManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.rolesWorkspace,
  );
  const roleManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.roleForm,
  );
  const profileWorkspaceManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.profilesWorkspace,
  );
  const permissionCatalogManager = useGenericManager('Permission');
  const workspaceRef = useRef(workspaceManager);
  const roleRef = useRef(roleManager);
  const profileWorkspaceRef = useRef(profileWorkspaceManager);
  const permissionCatalogRef = useRef(permissionCatalogManager);
  const [workspace, setWorkspace] = useState(null);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [roleMetadata, setRoleMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [roleDraft, setRoleDraft] = useState(null);
  const [roleCreateOpen, setRoleCreateOpen] = useState(false);
  const [roleCreateError, setRoleCreateError] = useState('');
  const [roleManagerOpen, setRoleManagerOpen] = useState(false);
  const roleManagerRef = useRef(null);
  const [roleEditDraft, setRoleEditDraft] = useState(null);
  const [roleDetailsUndo, setRoleDetailsUndo] = useState(null);
  const [rolePermissionDrafts, setRolePermissionDrafts] = useState([]);
  const [roleAIRefresh, setRoleAIRefresh] = useState(null);
  const [roleSettingsOpen, setRoleSettingsOpen] = useState(false);
  const [roleCloneOpen, setRoleCloneOpen] = useState(false);
  const [cloneDraft, setCloneDraft] = useState(null);
  const [roleDeleteOpen, setRoleDeleteOpen] = useState(false);
  const [roleDeleteTarget, setRoleDeleteTarget] = useState(null);
  const [roleUsersOpen, setRoleUsersOpen] = useState(false);
  const [roleUsersLoading, setRoleUsersLoading] = useState(false);
  const [roleUsersBusy, setRoleUsersBusy] = useState(false);
  const [roleUsersWorkspace, setRoleUsersWorkspace] = useState(null);
  const [selectedRoleUserKeys, setSelectedRoleUserKeys] = useState([]);
  const [roleUserRemovalTarget, setRoleUserRemovalTarget] = useState(null);
  const [roleMembershipError, setRoleMembershipError] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const toastTimerRef = useRef(null);
  workspaceRef.current = workspaceManager;
  roleRef.current = roleManager;
  profileWorkspaceRef.current = profileWorkspaceManager;
  permissionCatalogRef.current = permissionCatalogManager;

  const request = useCallback(
    (overrides = {}) => createSecurityRequest({
      workspace,
      clientId,
      appId,
      currentEnvironment,
      overrides,
    }),
    [appId, clientId, currentEnvironment, workspace],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [loaded, roleResult, permissionCatalogResult] = await Promise.all([
        runOperation(
          workspaceRef.current,
          CLIENT_SECURITY_OPERATIONS.loadRoles,
          createSecurityRequest({
            workspace: null,
            clientId,
            appId,
            currentEnvironment,
          }),
          'Client Roles & Permissions is unavailable.',
        ),
        roleRef.current.GetMetadata({ forceReload: true }),
        permissionCatalogRef.current?.Query
          ? permissionCatalogRef.current.Query({
            model: 'x => x.IsDeleted == false && x.IsActive == true',
          })
          : Promise.resolve(null),
      ]);
      setWorkspace(loaded);
      setRoleMetadata(getFirstMetadata(roleResult, roleRef.current));
      setPermissionCatalog(
        permissionCatalogResult?.Success
          && Array.isArray(permissionCatalogResult?.Items)
          ? permissionCatalogResult.Items
          : [],
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client Roles & Permissions is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => () => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
  }, []);

  const showSuccessToast = useCallback((nextMessage) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToastMessage(nextMessage);
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage('');
      toastTimerRef.current = null;
    }, 4000);
  }, []);

  const roleGridMetadata = useMutableSecurityGridMetadata(
    CLIENT_ROLE_GRID_METADATA,
  );
  const roleFields = useMemo(
    () => permissionSetFormFields(
      selectSecurityFields(roleMetadata, CLIENT_ROLE_FIELDS),
      workspace?.PermissionSets,
    ),
    [roleMetadata, workspace?.PermissionSets],
  );
  const selectedRole = (workspace?.Roles || []).find(
    (role) => role.Id === workspace?.SelectedRoleId,
  );
  const canCreateRole = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.createClientRoleFromPermissionSet,
  );
  const canCloneRole = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.cloneClientRole,
  );
  const canSaveRoleDetails = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientRoleDetails,
  );
  const canSaveRolePermission = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientRolePermission,
  );
  const canAssignRoles = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.assignClientUserRoles,
  );
  const canSetRoleLifecycle = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.setClientRoleLifecycle,
  );
  const canManageRoleDesign = canSaveRoleDetails && canSaveRolePermission;
  const isGlobalRole = (role) => (
    role?.ClientId == null
    || !String(role.ClientId).trim()
  );
  const isProtectedRole = (role) => (
    role?.IsProtected === true
    || role?.IsResourceBound === true
    || Boolean(String(role?.BoundClientResourceId || '').trim())
  );
  const selectedRoleEditable = selectedRole
    && selectedRole.SourcePermissionSet !== 'Default role';
  const roleEditFields = useMemo(
    () => selectSecurityFields(roleMetadata, CLIENT_ROLE_EDIT_FIELDS),
    [roleMetadata],
  );
  const cloneFields = useMemo(() => {
    const fields = selectSecurityFields(roleMetadata, CLIENT_ROLE_FIELDS);
    return selectedRoleEditable
      ? fields.filter((field) => (
        String(field?.FieldName).toLowerCase() !== 'permissionsetid'
      ))
      : permissionSetFormFields(fields, workspace?.PermissionSets);
  }, [roleMetadata, selectedRoleEditable, workspace?.PermissionSets]);
  const permissionGridMetadata = useMutableSecurityGridMetadata(
    CLIENT_PERMISSION_GRID_METADATA,
  );
  const assignedUsersGridMetadata = useMutableSecurityGridMetadata(
    CLIENT_ASSIGNED_USERS_GRID_METADATA,
  );
  const roleUsersGridMetadata = useMutableSecurityGridMetadata(
    CLIENT_PROFILE_GRID_METADATA,
  );
  const roleCardFields = useMemo(
    () => selectSecurityFields(roleGridMetadata[0], [
      'RoleCode',
      'Status',
      'Precedence',
      'AssignedUserCount',
    ]),
    [roleGridMetadata],
  );
  const permissionCardFields = useMemo(
    () => selectSecurityFields(permissionGridMetadata[0], [
      'Permission',
      'Effective',
      'Dependencies',
    ]),
    [permissionGridMetadata],
  );
  const assignedUserCardFields = useMemo(
    () => selectSecurityFields(assignedUsersGridMetadata[0], [
      'Name',
      'Email',
      'Status',
      'Roles',
    ]),
    [assignedUsersGridMetadata],
  );
  const roleUserCardFields = useMemo(
    () => selectSecurityFields(roleUsersGridMetadata[0], [
      'Name',
      'Email',
      'ConvokeProfileStatus',
      'AssignedRoles',
    ]),
    [roleUsersGridMetadata],
  );
  const assignedRoleValueDisplayResolvers = useMemo(() => {
    const roleNamesByCode = new Map(
      (workspace?.Roles || [])
        .filter((role) => role?.RoleCode && role?.RoleName)
        .map((role) => [
          String(role.RoleCode).toLowerCase(),
          String(role.RoleName).trim(),
        ]),
    );
    const resolveRoleNames = (value) => {
      const roleCodes = (Array.isArray(value) ? value : String(value || '').split(','))
        .map((roleCode) => String(roleCode).trim())
        .filter(Boolean);
      const roleNames = [];
      let unavailableRoleNameCount = 0;
      roleCodes.forEach((roleCode) => {
        const roleName = roleNamesByCode.get(roleCode.toLowerCase());
        if (roleName) roleNames.push(roleName);
        else unavailableRoleNameCount += 1;
      });
      if (unavailableRoleNameCount) {
        roleNames.push(
          `${unavailableRoleNameCount} role ${
            unavailableRoleNameCount === 1 ? 'name is' : 'names are'
          } unavailable`,
        );
      }
      return roleNames.join(', ');
    };
    return {
      Roles: resolveRoleNames,
      AssignedRoles: resolveRoleNames,
    };
  }, [workspace?.Roles]);
  const rolePermissionCatalog = useMemo(
    () => (permissionCatalog.length
      ? permissionCatalog
      : workspace?.Permissions || []).map((permission) => ({
      Permission: permission.Permission,
      DisplayName: permission.DisplayName || permission.Permission,
      Description: permission.Description || '',
      PermissionType: permission.PermissionType ?? 0,
      CanView: permission.DefaultCanView ?? permission.CanView ?? false,
      CanCreate: permission.DefaultCanCreate ?? permission.CanCreate ?? false,
      CanEdit: permission.DefaultCanEdit ?? permission.CanEdit ?? false,
      CanDelete: permission.DefaultCanDelete ?? permission.CanDelete ?? false,
      CanHardDelete: permission.DefaultCanHardDelete
        ?? permission.CanHardDelete
        ?? false,
      CanExecute: permission.DefaultCanExecute ?? permission.CanExecute ?? false,
      CanAccess: permission.DefaultCanAccess ?? permission.CanAccess ?? false,
      CanViewOnNav: permission.DefaultCanViewOnNav
        ?? permission.CanViewOnNav
        ?? false,
      DisableView: permission.DisableView === true,
      DisableCreate: permission.DisableCreate === true,
      DisableEdit: permission.DisableEdit === true,
      DisableDelete: permission.DisableDelete === true,
      DisableHardDelete: permission.DisableHardDelete === true,
      DisableExecute: permission.DisableExecute === true,
      DisableAccess: permission.DisableAccess === true,
      DisableViewOnNav: permission.DisableViewOnNav === true,
      PermissionDependencies: permission.PermissionDependencies || {},
    })),
    [permissionCatalog, workspace?.Permissions],
  );
  const rolePermissionSet = useMemo(() => ({
    _id: workspace?.SelectedRoleId
      ? `Role:${workspace.SelectedRoleId}`
      : '',
    Name: workspace?.RoleForm?.RoleName || selectedRole?.RoleName || '',
    Description: workspace?.RoleForm?.Description || selectedRole?.Description || '',
    Entries: (workspace?.Permissions || [])
      .filter(hasGrantedRoleCapability)
      .map(toRolePermissionEntry),
  }), [selectedRole, workspace?.Permissions, workspace?.RoleForm, workspace?.SelectedRoleId]);
  const roleEditorPayload = useMemo(() => ({
    ...rolePermissionSet,
    Name: roleEditDraft?.RoleName || rolePermissionSet.Name,
    Description: roleEditDraft?.Description || rolePermissionSet.Description,
    Entries: rolePermissionDrafts
      .filter(hasGrantedRoleCapability)
      .map(toRolePermissionEntry),
  }), [roleEditDraft, rolePermissionDrafts, rolePermissionSet]);
  const hasRoleDetailChanges = roleDetailsChanged(
    workspace?.RoleForm,
    roleEditDraft,
  );
  const loadRole = async (role) => {
    setBusy(true);
    setError('');
    try {
      const updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.loadRoles,
        request({ SelectedRoleId: role.Id }),
        'The selected role could not be loaded.',
      );
      setWorkspace(updated);
    } catch (ex) {
      setError(ex?.message || 'The selected role could not be loaded.');
    } finally {
      setBusy(false);
    }
  };

  const closeRoleUsers = () => {
    setRoleUsersOpen(false);
    setRoleUsersLoading(false);
    setRoleUsersWorkspace(null);
    setSelectedRoleUserKeys([]);
  };

  const openRoleUsers = async () => {
    if (!selectedRole) return;
    setRoleUsersOpen(true);
    setRoleUsersLoading(true);
    setRoleUsersWorkspace(null);
    setSelectedRoleUserKeys([]);
    setRoleMembershipError(null);
    try {
      const loaded = await runOperation(
        profileWorkspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.loadProfiles,
        createSecurityRequest({
          workspace: null,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Client users could not be loaded.',
      );
      setRoleUsersWorkspace(loaded);
    } catch (ex) {
      setRoleMembershipError({
        title: 'Role users could not be loaded',
        message: ex?.message || 'Client users could not be loaded.',
      });
    } finally {
      setRoleUsersLoading(false);
    }
  };

  const getProfileId = (profile) => profile?.Id || profile?.ProfileId || '';
  const getProfileName = (profile) => profile?.Name
    || profile?.DisplayName
    || profile?.Email
    || 'The user';
  const getAssignedRoleCodes = (profile) => (
    Array.isArray(profile?.AssignedRoles)
      ? profile.AssignedRoles
      : Array.isArray(profile?.Roles)
        ? profile.Roles
        : []
  );

  const applyRoleMembership = async (
    profile,
    assign,
    currentProfilesWorkspace,
  ) => {
    const profileId = profile?.Id || profile?.ProfileId;
    const roleCode = selectedRole?.RoleCode;
    if (!profileId || !roleCode) {
      throw new Error('The selected user or role is unavailable.');
    }
    const assignedRoles = getAssignedRoleCodes(profile);
    const hasRole = assignedRoles.some((candidate) => (
      String(candidate).toLowerCase() === String(roleCode).toLowerCase()
    ));
    const requestedRoleCodes = assign
      ? (hasRole ? assignedRoles : [...assignedRoles, roleCode])
      : assignedRoles.filter((candidate) => (
        String(candidate).toLowerCase() !== String(roleCode).toLowerCase()
      ));

    return runOperation(
      profileWorkspaceRef.current,
      CLIENT_SECURITY_OPERATIONS.assignRoles,
      createSecurityRequest({
        workspace: currentProfilesWorkspace,
        clientId,
        appId,
        currentEnvironment,
        overrides: {
          SelectedProfileId: profileId,
          RequestedRoleCodes: requestedRoleCodes,
        },
      }),
      assign
        ? 'The user could not be added to this role.'
        : 'The user could not be removed from this role.',
    );
  };

  const refreshRoleWorkspaceAfterMembershipChange = async () => {
    const updatedRoles = await runOperation(
      workspaceRef.current,
      CLIENT_SECURITY_OPERATIONS.loadRoles,
      createSecurityRequest({
        workspace,
        clientId,
        appId,
        currentEnvironment,
        overrides: { SelectedRoleId: workspace?.SelectedRoleId },
      }),
      'The role membership was updated, but the role view could not be refreshed.',
    );
    setWorkspace(updatedRoles);
    return updatedRoles;
  };

  const updateRoleMembership = async (profile, assign) => {
    setRoleUsersBusy(true);
    setRoleMembershipError(null);
    setToastMessage('');
    try {
      const updatedProfiles = await applyRoleMembership(
        profile,
        assign,
        roleUsersWorkspace,
      );
      setRoleUsersWorkspace(updatedProfiles);
      try {
        await refreshRoleWorkspaceAfterMembershipChange();
      } catch (ex) {
        setRoleMembershipError({
          title: 'Role view could not be refreshed',
          changed: true,
          message: ex?.message
            || 'The role membership was updated, but the role view could not be refreshed.',
        });
        return updatedProfiles;
      }

      showSuccessToast(
        updatedProfiles?.StatusMessage
          || `${getProfileName(profile)} was ${
            assign ? 'added to' : 'removed from'
          } ${selectedRole.RoleName}.`,
      );
      return updatedProfiles;
    } catch (ex) {
      setRoleMembershipError({
        title: assign
          ? 'User could not be added to the role'
          : 'User could not be removed from the role',
        message: ex?.message || (
          assign
            ? 'The user could not be added to this role.'
            : 'The user could not be removed from this role.'
        ),
      });
      return null;
    } finally {
      setRoleUsersBusy(false);
    }
  };

  const openRoleUserRemoval = (user) => {
    if (!user || !selectedRole) return;
    setRoleUserRemovalTarget({
      user,
      roleName: selectedRole.RoleName,
    });
  };

  const closeRoleUserRemoval = () => {
    if (roleUsersBusy) return;
    setRoleUserRemovalTarget(null);
  };

  const confirmRoleUserRemoval = async () => {
    const targetUser = roleUserRemovalTarget?.user;
    if (!targetUser) {
      return {
        Success: false,
        DisplayMessage: 'Select an assigned user to remove from the role.',
      };
    }

    await updateRoleMembership(targetUser, false);
    setRoleUserRemovalTarget(null);
    return { Success: true };
  };

  const selectedRoleIsAssignable = !roleUsersWorkspace
    || (roleUsersWorkspace.AssignableRoles || []).some((role) => (
      String(role.RoleCode).toLowerCase()
        === String(selectedRole?.RoleCode || '').toLowerCase()
    ));
  const isRoleAssigned = (profile) => (
    (profile?.AssignedRoles || []).some((roleCode) => (
      String(roleCode).toLowerCase()
        === String(selectedRole?.RoleCode || '').toLowerCase()
    ))
  );
  const availableRoleUserRows = (roleUsersWorkspace?.Profiles || []).filter(
    (profile) => !isRoleAssigned(profile),
  );

  const saveSelectedRoleUsers = async () => {
    if (!selectedRoleUserKeys.length) {
      return {
        Success: false,
        DisplayMessage: 'Select at least one user to add.',
      };
    }

    const availableProfilesById = new Map(availableRoleUserRows.map(
      (profile) => [String(getProfileId(profile)), profile],
    ));
    const selectedProfiles = selectedRoleUserKeys.map(
      (key) => availableProfilesById.get(String(key)),
    );
    if (selectedProfiles.some((profile) => !profile)) {
      return {
        Success: false,
        DisplayMessage:
          'One or more selected users are no longer available. Close and reopen Add Users, then try again.',
      };
    }
    const selectedUserSubject = selectedProfiles.length === 1
      ? 'user was'
      : 'users were';

    setRoleUsersBusy(true);
    setRoleMembershipError(null);
    setToastMessage('');
    let updatedProfilesWorkspace = roleUsersWorkspace;
    const appliedKeys = [];

    try {
      for (const profile of selectedProfiles) {
        try {
          updatedProfilesWorkspace = await applyRoleMembership(
            profile,
            true,
            updatedProfilesWorkspace,
          );
          appliedKeys.push(getProfileId(profile));
        } catch (ex) {
          setRoleUsersWorkspace(updatedProfilesWorkspace);
          setSelectedRoleUserKeys(selectedRoleUserKeys.filter(
            (key) => !appliedKeys.some(
              (appliedKey) => String(appliedKey) === String(key),
            ),
          ));

          let refreshFailure = '';
          if (appliedKeys.length) {
            try {
              await refreshRoleWorkspaceAfterMembershipChange();
            } catch (refreshEx) {
              refreshFailure = (
                ' The earlier changes remain applied, but the role view could '
                + `not be refreshed: ${
                  refreshEx?.message || 'Unknown refresh error.'
                }`
              );
            }
          }

          const appliedMessage = appliedKeys.length
            ? `${appliedKeys.length} of ${selectedProfiles.length} selected ${
              selectedUserSubject
            } added before this failure; those earlier membership changes remain applied.`
            : 'No selected users were added.';
          return {
            Success: false,
            DisplayMessage: (
              `${getProfileName(profile)} could not be added to ${
                selectedRole.RoleName
              }. ${
                ex?.message || 'The user could not be added to this role.'
              } ${appliedMessage}${refreshFailure}`
            ),
          };
        }
      }

      setRoleUsersWorkspace(updatedProfilesWorkspace);
      setSelectedRoleUserKeys([]);
      try {
        await refreshRoleWorkspaceAfterMembershipChange();
      } catch (ex) {
        return {
          Success: false,
          DisplayMessage: (
            `All ${selectedProfiles.length} selected ${selectedUserSubject} `
            + 'added, but the role view could not be refreshed. The membership '
            + `changes remain applied. ${ex?.message || ''}`
          ).trim(),
        };
      }

      closeRoleUsers();
      showSuccessToast(
        `${selectedProfiles.length} ${selectedUserSubject} added to ${
          selectedRole.RoleName
        }.`,
      );
      return { Success: true };
    } finally {
      setRoleUsersBusy(false);
    }
  };

  const openRoleManager = () => {
    setRoleEditDraft(cloneSecurityValue(workspace.RoleForm));
    setRoleDetailsUndo(null);
    setRolePermissionDrafts(cloneSecurityValue(workspace.Permissions || []));
    setRoleAIRefresh(null);
    setRoleManagerOpen(true);
  };
  const runRoleWorkspaceOperation = async (
    operationName,
    overrides,
    fallbackMessage,
  ) => {
    const updated = await runOperation(
      workspaceRef.current,
      operationName,
      createSecurityRequest({
        workspace,
        clientId,
        appId,
        currentEnvironment,
        overrides,
      }),
      fallbackMessage,
    );
    setWorkspace(updated);
    return updated;
  };

  const saveRoleDesign = async (updatedRolePermissionSet = null) => {
    setBusy(true);
    setError('');
    try {
      const updatedEntries = expandRolePermissionDependencies(
        Array.isArray(updatedRolePermissionSet?.Entries)
        ? updatedRolePermissionSet.Entries
        : rolePermissionDrafts.filter(hasGrantedRoleCapability),
        rolePermissionCatalog,
      );
      const nextPermissionDrafts = (workspace?.Permissions || []).map((permission) => {
        const entry = updatedEntries.find((candidate) => (
          String(candidate?.Permission).toLowerCase()
            === String(permission.Permission).toLowerCase()
        ));
        return {
          ...permission,
          ...ROLE_PERMISSION_CAPABILITIES.reduce((result, key) => ({
            ...result,
            [key]: entry?.[key] === true,
          }), {}),
          ApplyDependencies: entry?.ApplyDependencies !== false,
        };
      });
      let updated = await runRoleWorkspaceOperation(
        CLIENT_SECURITY_OPERATIONS.saveRole,
        { RoleForm: roleEditDraft },
        'The role details could not be saved.',
      );
      const changedPermissions = nextPermissionDrafts.filter((draft) => {
        const saved = (workspace?.Permissions || []).find(
          (permission) => permission.Permission === draft.Permission,
        );
        return saved && !samePermissionCapabilities(saved, draft);
      });
      for (const draft of changedPermissions) {
        const saved = (updated?.Permissions || []).find(
          (permission) => permission.Permission === draft.Permission,
        );
        updated = await runOperation(
          workspaceRef.current,
          CLIENT_SECURITY_OPERATIONS.savePermission,
          createSecurityRequest({
            workspace: updated,
            clientId,
            appId,
            currentEnvironment,
            overrides: {
              PermissionForm: {
                ...draft,
                RoleId: updated.SelectedRoleId,
                ExpectedVersion: saved?.ConcurrencyToken || draft.ConcurrencyToken,
              },
            },
          }),
          `The ${draft.DisplayName || draft.Permission} permission could not be saved.`,
        );
      }
      setWorkspace(updated);
      setRoleEditDraft(cloneSecurityValue(updated.RoleForm));
      setRoleDetailsUndo(null);
      setRolePermissionDrafts(cloneSecurityValue(updated.Permissions || []));
      return { Success: true, Items: [updated] };
    } catch (ex) {
      setError(ex?.message || 'The role design could not be saved.');
      return { Success: false, Message: ex?.message };
    } finally {
      setBusy(false);
    }
  };

  const openCloneRole = () => {
    setCloneDraft({
      ...cloneSecurityValue(workspace.RoleForm),
      RoleId: '',
      RoleCode: `${workspace.RoleForm.RoleCode}Copy`,
      RoleName: `${workspace.RoleForm.RoleName} Copy`,
      ExpectedVersion: '',
      Activate: true,
      Precedence: Math.max(
        Number(workspace.RoleForm.Precedence)
          || DEFAULT_CLIENT_ROLE_PRECEDENCE,
        MINIMUM_CLIENT_ROLE_PRECEDENCE,
      ),
      PermissionSetId: selectedRoleEditable
        ? workspace.RoleForm.PermissionSetId
        : workspace?.PermissionSets?.[0]?.Id
          || NO_PERMISSION_SET_VALUE,
    });
    setRoleCloneOpen(true);
  };

  const openDeleteRole = (role) => {
    if (!role) return;
    setRoleDeleteTarget(role);
    setRoleDeleteOpen(true);
  };

  const roleDeleteBlocker = useMemo(() => {
    if (!roleDeleteTarget) return '';
    const assignedUsers = Number(roleDeleteTarget.AssignedUserCount) || 0;
    const mappings = Number(roleDeleteTarget.MappingCount) || 0;
    if (!assignedUsers && !mappings) return '';

    const blockers = [];
    if (assignedUsers) {
      blockers.push(
        `${assignedUsers} assigned ${assignedUsers === 1 ? 'user' : 'users'}`,
      );
    }
    if (mappings) {
      blockers.push(
        `${mappings} identity ${mappings === 1 ? 'mapping' : 'mappings'}`,
      );
    }
    const removalTarget = assignedUsers && mappings
      ? 'those assignments and mappings'
      : assignedUsers
        ? 'the user assignment'
        : 'the identity mapping';
    return `${roleDeleteTarget.RoleName || 'This role'} cannot be deleted while it has ${
      blockers.join(' and ')
    }. Remove ${removalTarget} first.`;
  }, [roleDeleteTarget]);

  const closeDeleteRole = () => {
    setRoleDeleteOpen(false);
    setRoleDeleteTarget(null);
  };

  const deleteRole = async () => {
    if (!roleDeleteTarget?.Id) {
      return {
        Success: false,
        DisplayMessage: 'Select a client-owned role to delete.',
      };
    }
    setBusy(true);
    try {
      await runRoleWorkspaceOperation(
        CLIENT_SECURITY_OPERATIONS.setRoleLifecycle,
        {
          SelectedRoleId: roleDeleteTarget?.Id,
          LifecycleAction: 'remove',
          ConfirmLifecycleImpact: true,
        },
        'The client role could not be deleted.',
      );
      setRoleDeleteOpen(false);
      setRoleDeleteTarget(null);
      setRoleManagerOpen(false);
      setRoleSettingsOpen(false);
      setRoleEditDraft(null);
      setRolePermissionDrafts([]);
      showSuccessToast(
        `${roleDeleteTarget.RoleName || 'The role'} was deleted.`,
      );
      return { Success: true };
    } catch (ex) {
      return {
        Success: false,
        DisplayMessage:
          ex?.message || 'The client role could not be deleted.',
      };
    } finally {
      setBusy(false);
    }
  };

  const roleDeleteActions = canSetRoleLifecycle ? [{
    Key: 'delete-role',
    Title: 'Delete Role',
    IconKey: 'delete',
    Tone: 'error',
    Disabled: (role) => (
      busy
      || isGlobalRole(role)
      || isProtectedRole(role)
    ),
    ActionCallback: openDeleteRole,
  }] : undefined;

  const roleActions = selectedRole ? [
    ...(canManageRoleDesign ? [{
    key: 'open-role-manager',
    label: 'Open Role Manager',
    iconKey: 'settings',
    disabled: loading || busy,
    onClick: openRoleManager,
    }] : []),
    ...((selectedRoleEditable ? canCloneRole : canCreateRole) ? [{
    key: 'clone-role',
    label: 'Clone Role',
    iconKey: 'clone',
    disabled: loading || busy || !roleMetadata,
    onClick: openCloneRole,
    }] : []),
  ] : [];

  const cloneRole = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await runRoleWorkspaceOperation(
        selectedRoleEditable
          ? CLIENT_SECURITY_OPERATIONS.cloneRole
          : CLIENT_SECURITY_OPERATIONS.createRole,
        {
          SelectedRoleId: workspace.SelectedRoleId,
          RoleForm: cloneDraft,
        },
        selectedRoleEditable
          ? 'The client role could not be cloned.'
          : 'The editable client copy could not be created.',
      );
      setRoleCloneOpen(false);
      setRoleManagerOpen(false);
      setRoleEditDraft(cloneSecurityValue(updated.RoleForm));
      setRolePermissionDrafts(cloneSecurityValue(updated.Permissions || []));
      return updated;
    } finally {
      setBusy(false);
    }
  };

  const stageRoleSettings = async () => {
    setRoleSettingsOpen(false);
    return roleEditDraft;
  };

  const rollbackRoleSettings = ({ undo }) => {
    if (undo && roleDetailsUndo) {
      setRoleEditDraft(roleDetailsUndo);
      setRoleDetailsUndo(null);
      return;
    }
    setRoleDetailsUndo(cloneSecurityValue(roleEditDraft));
    setRoleEditDraft(cloneSecurityValue(workspace?.RoleForm));
  };

  const handleRoleEditorChange = useCallback((updatedRolePermissionSet) => {
    const entries = Array.isArray(updatedRolePermissionSet?.Entries)
      ? updatedRolePermissionSet.Entries
      : [];
    setRolePermissionDrafts((current) => current.map((permission) => {
      const entry = entries.find((candidate) => (
        String(candidate?.Permission).toLowerCase()
          === String(permission.Permission).toLowerCase()
      ));
      return {
        ...permission,
        ...ROLE_PERMISSION_CAPABILITIES.reduce((result, key) => ({
          ...result,
          [key]: entry?.[key] === true,
        }), {}),
        ApplyDependencies: entry?.ApplyDependencies !== false,
      };
    }));
  }, []);

  const handleRoleAIUpdate = (_uiCommand, item, value) => {
    const entries = extractPermissionEntries(item)
      || extractPermissionEntries(value);
    if (!entries) return;
    const next = rolePermissionDrafts.map((permission) => {
      const update = entries.find((entry) => (
        String(entry.Permission).toLowerCase()
          === String(permission.Permission).toLowerCase()
      ));
      return {
        ...permission,
        ...ROLE_PERMISSION_CAPABILITIES.reduce((result, key) => ({
          ...result,
          [key]: update?.[key] === true,
        }), {}),
        ApplyDependencies: update?.ApplyDependencies !== false,
      };
    });
    setRolePermissionDrafts(next);
    setRoleAIRefresh({
      ...rolePermissionSet,
      Entries: next
        .filter(hasGrantedRoleCapability)
        .map(toRolePermissionEntry),
      __aiAcceptedDraft: true,
      __refreshKey: Date.now(),
    });
  };

  const openCreateRole = () => {
    setRoleCreateError('');
    setRoleDraft({
      ClientId: clientId,
      RoleId: '',
      RoleCode: '',
      RoleName: '',
      Description: '',
      PermissionSetId: NO_PERMISSION_SET_VALUE,
      ExpectedVersion: '',
      Activate: true,
      Precedence: DEFAULT_CLIENT_ROLE_PRECEDENCE,
    });
    setRoleCreateOpen(true);
  };

  const updateCreateRolePermissionSet = (
    value,
    fieldName,
    updatedRoleDraft,
  ) => {
    if (String(fieldName).toLowerCase() !== 'permissionsetid') return;
    const permissionSet = value === NO_PERMISSION_SET_VALUE
      ? null
      : (workspace?.PermissionSets || []).find(
        (candidate) => String(candidate.Id) === String(value),
      );
    setRoleDraft({
      ...updatedRoleDraft,
      PermissionSetId: value,
      ...permissionSetRoleDefaults(permissionSet),
    });
  };

  const saveRole = async (updatedRoleDraft) => {
    setBusy(true);
    setRoleCreateError('');
    try {
      const updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.createRole,
        request({ RoleForm: updatedRoleDraft }),
        'The client role could not be created.',
      );
      setWorkspace(updated);
      setRoleCreateOpen(false);
      return updated;
    } catch (ex) {
      const message = ex?.message || 'The client role could not be created.';
      setRoleCreateError(message);
      return { Success: false, DisplayMessage: message };
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="resource-workspace-overview command-entry command-security">
      <SecurityToolbar
        busy={loading || busy}
        onRefresh={refresh}
        ariaLabel="Roles and Permissions actions"
        actions={roleActions}
      />
      <SecurityState
        loading={loading}
        error={error}
        message=""
        label="Client Roles & Permissions"
      />
      {toastMessage ? (
        <ToastNotifications position="top" message={toastMessage} />
      ) : null}
      {!loading && workspace ? (
        <>
          <div className="command-security__roles-workspace">
            <section className="resource-workspace-panel command-security__roles-column">
              <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                <div>
                  <h3>Roles</h3>
                  <p>Client and default roles. Select one to review its permissions and assigned users.</p>
                </div>
                {canCreateRole ? (
                  <div className="resource-workspace-section-actions">
                    <ButtonControl
                      type="icon"
                      className="command-security__add-role"
                      title="Create Role"
                      aria-label="Create Role"
                      disabled={busy || !roleMetadata}
                      onClick={openCreateRole}
                    >
                      <IconControl
                        iconKey="add"
                        title="Create Role"
                        width="1rem"
                        height="1rem"
                      />
                    </ButtonControl>
                  </div>
                ) : null}
              </div>
              <CardGridControl
                className="command-security__role-card-grid"
                rows={workspace.Roles || []}
                metadata={roleGridMetadata}
                cardFields={roleCardFields}
                rowKey={(role) => role.Id}
                cardTitleResolver={(role) => role.RoleName}
                selectable
                searchable
                selectedRowKeys={workspace.SelectedRoleId
                  ? [workspace.SelectedRoleId]
                  : []}
                onSelectedRowKeysChange={(_keys, role) => {
                  if (
                    role?.Id
                    && role.Id !== workspace.SelectedRoleId
                  ) {
                    loadRole(role);
                  }
                }}
                headerIconActions={roleDeleteActions}
                selectedHeaderIconActions={roleDeleteActions}
                disabled={busy}
                disableInlineEdit
                disableCardClickEdit
                activeFilterEnabled={false}
                emptyContent="No client or default roles are available."
                minCardWidth="15rem"
                maxColumns={1}
              />
            </section>

            <section className="resource-workspace-panel command-security__permissions-column">
              <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                <div>
                  <h3>Permissions</h3>
                  <p>
                    {selectedRole
                      ? `${selectedRole.RoleName} · Read-only permissions`
                      : 'Select a role to review its permissions.'}
                  </p>
                </div>
              </div>
              <CardGridControl
                className="command-security__permission-card-grid"
                rows={workspace.Permissions || []}
                metadata={permissionGridMetadata}
                cardFields={permissionCardFields}
                rowKey={(permission) => permission.Permission}
                cardTitleResolver={(permission) => (
                  permission.DisplayName || permission.Permission
                )}
                readOnly
                disabled={busy}
                disableInlineEdit
                disableCardClickEdit
                activeFilterEnabled={false}
                emptyContent={
                  selectedRole
                    ? 'No permissions are recorded for this role.'
                    : 'Select a role to review permissions.'
                }
                minCardWidth="15rem"
                maxColumns={1}
              />
            </section>

            <section className="resource-workspace-panel command-security__assigned-users-column">
              <div className="resource-workspace-section-title resource-workspace-section-title--with-action">
                <div>
                  <h3>Assigned users</h3>
                  <p>
                    {selectedRole
                      ? `Add or remove client users from ${selectedRole.RoleName}.`
                      : 'Select a role to view assigned users.'}
                  </p>
                </div>
                {selectedRole && canAssignRoles ? (
                  <div className="resource-workspace-section-actions">
                    <ButtonControl
                      type="icon"
                      className="command-security__manage-role-users"
                      title="Add Users"
                      aria-label="Add Users"
                      disabled={busy || roleUsersBusy}
                      onClick={openRoleUsers}
                    >
                      <IconControl
                        iconKey="add"
                        title="Add Users"
                        width="1rem"
                        height="1rem"
                      />
                    </ButtonControl>
                  </div>
                ) : null}
              </div>
              <CardGridControl
                className="command-security__assigned-user-card-grid"
                rows={workspace.AssignedUsers || []}
                metadata={assignedUsersGridMetadata}
                cardFields={assignedUserCardFields}
                valueDisplayResolvers={assignedRoleValueDisplayResolvers}
                rowKey={(user) => user.ProfileId}
                cardTitleResolver={(user) => user.Name}
                readOnly
                headerIconActions={selectedRole && canAssignRoles ? [{
                  Key: 'remove-role-user',
                  Title: 'Remove from role',
                  IconKey: 'delete',
                  Tone: 'error',
                  Disabled: (user) => roleUsersBusy || (
                    String(selectedRole.RoleCode || '').toLowerCase()
                      === CLIENT_ADMIN_ROLE_CODE.toLowerCase()
                    && user?.IsCurrentUser === true
                  ),
                  ActionCallback: openRoleUserRemoval,
                }] : undefined}
                disableInlineEdit
                disableCardClickEdit
                activeFilterEnabled={false}
                emptyContent={
                  selectedRole
                    ? 'No users are assigned to this role.'
                    : 'Select a role to view assigned users.'
                }
                minCardWidth="15rem"
                maxColumns={1}
              />
            </section>
          </div>
        </>
      ) : null}

      <SidePanelFormControl
        open={roleCreateOpen}
        title="Create Role"
        metadata={roleMetadata}
        formFields={roleFields}
        itemData={roleDraft}
        onChange={updateCreateRolePermissionSet}
        onSave={saveRole}
        onCancel={() => setRoleCreateOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Create Role"
        setOpenEditSidePanel={setRoleCreateOpen}
        setItemData={setRoleDraft}
        viewOnly={!canCreateRole}
      />
      <DialogControl
        openDialog={!!roleCreateError}
        title="Create Role"
        subTitle="The role was not created."
        okText="Close"
        cancelText=""
        onCancel={() => setRoleCreateError('')}
        onX={() => setRoleCreateError('')}
        onOk={() => setRoleCreateError('')}
      >
        <ConvokeDialogMessage
          message={roleCreateError}
          tone="error"
        />
      </DialogControl>

      <DialogControl
        openDialog={roleUsersOpen}
        title="Add Users"
        subTitle={selectedRole
          ? `Search for client users to add to ${selectedRole.RoleName}.`
          : ''}
        okText="Save"
        cancelText="Cancel"
        disableOk={
          roleUsersLoading
          || roleUsersBusy
          || !selectedRoleIsAssignable
          || selectedRoleUserKeys.length === 0
        }
        disableCancel={roleUsersBusy}
        disableXButton={roleUsersBusy}
        loadingOk={roleUsersBusy}
        onCancel={closeRoleUsers}
        onX={closeRoleUsers}
        onOk={saveSelectedRoleUsers}
      >
        {roleUsersLoading ? (
          <ResourceStateControl
            compact
            loading
            title="Loading client users"
            loadingText="Loading the client user list."
          />
        ) : !selectedRoleIsAssignable ? (
          <ConvokeDialogMessage
            message="This protected role cannot be assigned or removed manually."
          />
        ) : (
          <CardGridControl
            className="command-security__role-user-card-grid"
            rows={availableRoleUserRows}
            metadata={roleUsersGridMetadata}
            cardFields={roleUserCardFields}
            valueDisplayResolvers={assignedRoleValueDisplayResolvers}
            rowKey={(profile) => profile.Id}
            cardTitleResolver={(profile) => profile.Name || profile.DisplayName}
            searchable
            selectable
            multiSelect
            selectedRowKeys={selectedRoleUserKeys}
            onSelectedRowKeysChange={setSelectedRoleUserKeys}
            disabled={roleUsersBusy}
            disableInlineEdit
            disableCardClickEdit
            activeFilterEnabled={false}
            emptyContent="All available client users are already assigned to this role."
            minCardWidth="15rem"
            maxColumns={1}
          />
        )}
      </DialogControl>

      <DialogControl
        openDialog={!!roleUserRemovalTarget}
        title="Remove user from role"
        subTitle="Confirm this role membership change."
        okText="Remove from role"
        okType="danger"
        cancelText="Cancel"
        loadingOk={roleUsersBusy}
        disableOk={roleUsersBusy}
        disableCancel={roleUsersBusy}
        disableXButton={roleUsersBusy}
        onCancel={closeRoleUserRemoval}
        onX={closeRoleUserRemoval}
        onOk={confirmRoleUserRemoval}
      >
        <ConvokeDialogMessage
          message={`${getProfileName(roleUserRemovalTarget?.user)} will be removed from ${
            roleUserRemovalTarget?.roleName || 'this role'
          }. Their other assigned roles will be preserved.`}
        />
      </DialogControl>

      <DialogControl
        openDialog={!!roleMembershipError}
        title={roleMembershipError?.title || 'Role membership could not be updated'}
        subTitle={roleMembershipError?.changed
          ? 'The membership was updated, but the screen could not be refreshed.'
          : 'The role membership was not changed.'}
        okText="Close"
        cancelText=""
        onCancel={() => setRoleMembershipError(null)}
        onX={() => setRoleMembershipError(null)}
        onOk={() => setRoleMembershipError(null)}
      >
        <ConvokeDialogMessage
          message={roleMembershipError?.message}
          tone="error"
        />
      </DialogControl>

      <DialogControl
        openDialog={roleDeleteOpen}
        title="Delete Role"
        subTitle={roleDeleteBlocker
          ? 'This role is still in use.'
          : 'Delete this client-owned role?'}
        okText={roleDeleteBlocker ? 'Close' : 'Delete Role'}
        okType={roleDeleteBlocker ? undefined : 'danger'}
        cancelText="Cancel"
        hideCancel={!!roleDeleteBlocker}
        onCancel={closeDeleteRole}
        onX={closeDeleteRole}
        onOk={roleDeleteBlocker ? closeDeleteRole : deleteRole}
      >
        <ConvokeDialogMessage
          message={roleDeleteBlocker || (
            `${roleDeleteTarget?.RoleName || 'This role'} will be deleted. `
            + 'Roles with assigned users or Entra mappings must be cleared first.'
          )}
          tone={roleDeleteBlocker ? 'error' : undefined}
        />
      </DialogControl>

      <AIPanel
        childrenRef={roleManagerRef}
        open={roleManagerOpen}
        presentation="slideout"
        mapToClass="core-layout"
        title="Manage Role"
        className="ai-panel-floating-slideout"
        onCancel={() => setRoleManagerOpen(false)}
        onUpdatePanel={handleRoleAIUpdate}
        operationName={selectedRoleEditable ? 'AIPermissionSet' : undefined}
        referenceId={workspace?.SelectedRoleId}
        referenceName="RoleManage"
        params={{
          id: workspace?.SelectedRoleId,
          permissionSetId: workspace?.RoleForm?.PermissionSetId,
          roleId: workspace?.SelectedRoleId,
          draftOnly: selectedRoleEditable ? 'true' : '',
          clientId,
          applicationId: appId,
          currentEnvironment,
        }}
        payload={roleEditorPayload}
        reviewContext={{ permissions: rolePermissionCatalog }}
        enableContentDragHiding={false}
        defaultOrchestrationMode="AgentAssist"
        singleSessionMode
        chatPaneToggleEnabled
        chatPaneCollapseMode="auto"
        planningPreviewEnabled
        previewPanelEnabled
        previewRefreshEnabled={false}
        splashMessage="How can I help you manage this role?"
        introMessage="Manage Role..."
        width={1225}
        minPanelWidth={1225}
        panelStorageKey={null}
        minLeft={825}
        minRight={400}
      >
        <div className="permission-set-editor" ref={roleManagerRef}>
          {!selectedRoleEditable ? (
            <div className="command-security__role-manager-message">
              <ConvokeDialogMessage
                message="Shared default roles cannot be edited directly. Close Role Manager and use Clone Role from the Roles & Permissions actions to create an editable client copy."
              />
            </div>
          ) : null}
          <PermissionSetEditor
            permissionSet={rolePermissionSet}
            headerItem={roleEditorPayload}
            permissions={rolePermissionCatalog}
            refresh={roleAIRefresh}
            onSave={saveRoleDesign}
            onChange={handleRoleEditorChange}
            onRollback={rollbackRoleSettings}
            readOnly={!selectedRoleEditable || !canManageRoleDesign}
            hasExternalChanges={hasRoleDetailChanges}
            headerActions={selectedRoleEditable && canManageRoleDesign ? [
              {
                key: 'role-settings',
                label: 'Role Settings',
                onClick: () => setRoleSettingsOpen(true),
                disabled: busy,
                title: 'Edit role name, description, icon, precedence, and default screen',
              },
            ] : []}
            currentEntriesSubtitle="Permissions granted by this role."
            availablePermissionsSubtitle="Add permissions to this role. Defaults are applied on add."
            searchPlaceholder="Search role permissions..."
            emptyEntriesMessage="No role permissions match your search."
          />
        </div>
      </AIPanel>

      <SidePanelFormControl
        open={roleSettingsOpen}
        title="Role Settings"
        subTitle="The role code and recorded Permission Set baseline remain fixed."
        metadata={roleMetadata}
        formFields={roleEditFields}
        itemData={roleEditDraft}
        onSave={stageRoleSettings}
        onCancel={() => setRoleSettingsOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Apply Role Settings"
        setOpenEditSidePanel={setRoleSettingsOpen}
        setItemData={setRoleEditDraft}
        viewOnly={!selectedRoleEditable || !canManageRoleDesign}
      />

      <SidePanelFormControl
        open={roleCloneOpen}
        title={selectedRoleEditable ? 'Clone Role' : 'Create Editable Client Copy'}
        subTitle={selectedRoleEditable
          ? 'Copies role details and permissions only. Assigned users and Entra mappings are not copied.'
          : 'Default roles are read-only. Choose an exact published Permission Set baseline for the editable client copy.'}
        metadata={roleMetadata}
        formFields={cloneFields}
        itemData={cloneDraft}
        onSave={cloneRole}
        onCancel={() => setRoleCloneOpen(false)}
        onUpdateSidePanel={() => {}}
        saveButtonText={selectedRoleEditable ? 'Clone Role' : 'Create Client Copy'}
        setOpenEditSidePanel={setRoleCloneOpen}
        setItemData={setCloneDraft}
        viewOnly={!(selectedRoleEditable ? canCloneRole : canCreateRole)}
      />

    </div>
  );
};

const getActiveDirectoryProviderMappings = (providerWorkspace) => (
  (providerWorkspace?.Mappings || []).filter((mapping) => (
    mapping?.IsActive
    && (!mapping.ProviderKey || mapping.ProviderKey === 'EntraID')
    && providerWorkspace?.ReadLogin?.ValidationStatus === 'Validated'
    && String(mapping.TenantId || '').toLowerCase()
      === String(providerWorkspace?.ReadLogin?.TenantId || '').toLowerCase()
    && String(mapping.AuthProviderClientId || '').toLowerCase()
      === String(providerWorkspace?.ReadLogin?.SignInClientId || '').toLowerCase()
  ))
);

export const ClientUserProfilesScreen = ({
  screen,
  clientId,
  appId,
  currentEnvironment,
}) => {
  const { canExecute } = useCorePermissions();
  const workspaceManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.profilesWorkspace,
  );
  const profileManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.profileForm,
  );
  const authenticationManager = useGenericManager(
    CLIENT_SECURITY_ITEM_NAMES.authenticationWorkspace,
  );
  const workspaceRef = useRef(workspaceManager);
  const profileRef = useRef(profileManager);
  const authenticationRef = useRef(authenticationManager);
  const [workspace, setWorkspace] = useState(null);
  const [authenticationWorkspace, setAuthenticationWorkspace] = useState(null);
  const [profileMetadata, setProfileMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const toastTimerRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchPurpose, setSearchPurpose] = useState('add');
  const [searchTargetProfile, setSearchTargetProfile] = useState(null);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [selectedProviderMappingId, setSelectedProviderMappingId] = useState('');
  const [providerLoadError, setProviderLoadError] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(null);
  const [repairDraft, setRepairDraft] = useState(null);
  const [accessAction, setAccessAction] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [guestInvitationsOpen, setGuestInvitationsOpen] = useState(false);
  workspaceRef.current = workspaceManager;
  profileRef.current = profileManager;
  authenticationRef.current = authenticationManager;

  const request = useCallback(
    (overrides = {}) => createSecurityRequest({
      workspace,
      clientId,
      appId,
      currentEnvironment,
      overrides,
    }),
    [appId, clientId, currentEnvironment, workspace],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    setToastMessage('');
    try {
      const [loaded, metadataResult] = await Promise.all([
        runOperation(
          workspaceRef.current,
          CLIENT_SECURITY_OPERATIONS.loadProfiles,
          createSecurityRequest({
            workspace: null,
            clientId,
            appId,
            currentEnvironment,
          }),
          'Client User Profiles is unavailable.',
        ),
        profileRef.current.GetMetadata({ forceReload: true }),
      ]);
      setWorkspace(loaded);
      setProfileMetadata(
        getFirstMetadata(metadataResult, profileRef.current),
      );
    } catch (ex) {
      setWorkspace(null);
      setError(ex?.message || 'Client User Profiles is unavailable.');
    } finally {
      setLoading(false);
    }
  }, [appId, clientId, currentEnvironment]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => () => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
  }, []);

  const showSuccessToast = useCallback((nextMessage) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToastMessage(nextMessage);
    toastTimerRef.current = window.setTimeout(() => {
      setToastMessage('');
      toastTimerRef.current = null;
    }, 4000);
  }, []);

  const showActionError = useCallback((title, message) => {
    setActionError({
      title: title || 'Action failed',
      message: message || 'The action could not be completed.',
    });
  }, []);

  const execute = async (
    operationName,
    operationRequest,
    successMessage,
    close = null,
    failureTitle = 'Action failed',
    failureMessage = 'The action could not be completed.',
  ) => {
    setBusy(true);
    setError('');
    setToastMessage('');
    try {
      const updated = await runOperation(
        workspaceRef.current,
        operationName,
        operationRequest,
        failureMessage,
      );
      setWorkspace(updated);
      showSuccessToast(updated?.StatusMessage || successMessage);
      if (close) close();
      return updated;
    } catch (ex) {
      showActionError(failureTitle, ex?.message || failureMessage);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const canSearchEntraUsers = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.searchClientEntraUsers,
  );
  const canSaveProfile = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.saveClientUserProfile,
  );
  const canAssignRoles = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.assignClientUserRoles,
  );
  const canSetUserAccess = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.setClientUserAccess,
  );
  const canRepairEntraMapping = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.repairClientUserEntraMapping,
  );
  const canLoadGuestAccess = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.loadClientGuestAccess,
  );
  const canInviteGuest = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.inviteClientGuest,
  );
  const canReconcileGuestAccess = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.reconcileClientGuestAccess,
  );
  const canRevokeGuest = canExecute(
    CLIENT_COMMAND_OPERATION_PERMISSIONS.revokeClientGuest,
  );
  const profileFields = useMemo(
    () => profileRoleFormFields(
      selectSecurityFields(profileMetadata, CLIENT_PROFILE_FIELDS),
      workspace?.AssignableRoles,
    ).filter((field) => canAssignRoles || field.FieldName !== 'RoleCodes'),
    [canAssignRoles, profileMetadata, workspace?.AssignableRoles],
  );
  const profileGridMetadata = useMutableSecurityGridMetadata(
    CLIENT_PROFILE_GRID_METADATA,
  );
  const directoryUserCardMetadata = useMutableSecurityGridMetadata(
    DIRECTORY_USER_CARD_METADATA,
  );
  const activeProviderMappings = useMemo(
    () => getActiveDirectoryProviderMappings(authenticationWorkspace),
    [authenticationWorkspace],
  );
  const activeProviderField = useMemo(() => ({
    ...ACTIVE_AUTH_PROVIDER_FIELD,
    DataJson: JSON.stringify(activeProviderMappings.map((mapping) => ({
      Text: mapping.DisplayName || 'Microsoft Entra ID',
      Value: mapping.Id,
    }))),
  }), [activeProviderMappings]);

  const loadDirectoryUsers = async (providerMappingId) => {
    if (!providerMappingId) return null;
    setBusy(true);
    setProviderLoadError('');
    try {
      const updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.searchEntraUsers,
        request({
          SearchText: '',
          SelectedProviderMappingId: providerMappingId,
        }),
        'Directory users are unavailable.',
      );
      setWorkspace(updated);
      setSearchAttempted(true);
      return updated;
    } catch (ex) {
      setProviderLoadError('');
      showActionError(
        'Directory users could not be loaded',
        ex?.message || 'Directory users are unavailable.',
      );
      setSearchAttempted(false);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const loadActiveProviders = async () => {
    setBusy(true);
    setProviderLoadError('');
    try {
      const loaded = await runOperation(
        authenticationRef.current,
        CLIENT_SECURITY_OPERATIONS.loadAuthentication,
        createSecurityRequest({
          workspace: null,
          clientId,
          appId,
          currentEnvironment,
        }),
        'Authentication providers are unavailable.',
      );
      const activeMappings = getActiveDirectoryProviderMappings(loaded);
      const preferredMapping = activeMappings.find(
        (mapping) => String(mapping.Id) === String(loaded?.DefaultMappingId),
      ) || activeMappings[0];
      setAuthenticationWorkspace(loaded);
      setSelectedProviderMappingId(preferredMapping?.Id || '');
      if (!preferredMapping) {
        setProviderLoadError(
          'No active validated authentication provider is configured for directory user lookup.',
        );
      }
      return preferredMapping;
    } catch (ex) {
      setAuthenticationWorkspace(null);
      setSelectedProviderMappingId('');
      setProviderLoadError('');
      showActionError(
        'Authentication providers could not be loaded',
        ex?.message || 'Authentication providers are unavailable.',
      );
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openUserSearch = async ({
    purpose = 'add',
    targetProfile = null,
  } = {}) => {
    setSearchPurpose(purpose);
    setSearchTargetProfile(targetProfile);
    setSearchAttempted(false);
    setProviderLoadError('');
    setWorkspace((current) => current
      ? { ...current, EntraSearchResults: [] }
      : current);
    setSearchOpen(true);
    const preferredMapping = await loadActiveProviders();
    if (preferredMapping?.Id) {
      await loadDirectoryUsers(preferredMapping.Id);
    }
  };

  const selectEntraUser = (user) => {
    if (searchPurpose === 'repair' && searchTargetProfile) {
      setRepairDraft({
        profile: searchTargetProfile,
        form: {
          ...(workspace?.ProfileForm || {}),
          ProfileId: searchTargetProfile.Id,
          EntraObjectId: user.ObjectId,
          UserPrincipalName: user.UserPrincipalName,
        },
      });
      setSearchOpen(false);
      return;
    }
    setProfileDraft({
      ...(workspace?.ProfileForm || {}),
      ProfileId: '',
      ExpectedVersion: '',
      EntraObjectId: user.ObjectId,
      UserPrincipalName: user.UserPrincipalName,
      DisplayName: user.DisplayName,
      Email: user.Email || user.UserPrincipalName,
      Address: normalizeClientProfileAddress(
        workspace?.ProfileForm?.Address,
        { defaultCountry: true },
      ),
      Locale: workspace?.ProfileForm?.Locale || DEFAULT_CLIENT_PROFILE_LOCALE,
      RoleCodes: [],
    });
    setSearchOpen(false);
    setProfileOpen(true);
  };

  const openNewProfile = () => {
    setProfileDraft({
      ...(workspace?.ProfileForm || {}),
      ProfileId: '',
      ExpectedVersion: '',
      EntraObjectId: '',
      UserPrincipalName: '',
      Address: normalizeClientProfileAddress(
        workspace?.ProfileForm?.Address,
        { defaultCountry: true },
      ),
      Locale: workspace?.ProfileForm?.Locale || DEFAULT_CLIENT_PROFILE_LOCALE,
      RoleCodes: [],
    });
    setProfileOpen(true);
  };

  const openProfile = (profileRow) => {
    const profileId = profileRow?.Id || profileRow?._id;
    const profile = (workspace?.Profiles || []).find(
      (candidate) => String(candidate?.Id || candidate?._id) === String(profileId),
    ) || profileRow;
    setProfileDraft({
      ...(workspace?.ProfileForm || {}),
      ProfileId: profile.Id,
      ExpectedVersion: profile.ConcurrencyToken,
      EntraObjectId: profile.EntraObjectId,
      UserPrincipalName: profile.UserPrincipalName,
      FirstName: profile.FirstName,
      MiddleName: profile.MiddleName,
      LastName: profile.LastName,
      DisplayName: profile.DisplayName ?? profile.Name ?? '',
      Email: profile.Email,
      SecondEmail: profile.SecondEmail,
      PrimaryPhone: profile.PrimaryPhone,
      MobilePhone: profile.MobilePhone,
      Address: normalizeClientProfileAddress(
        cloneSecurityValue(profile.Address),
      ),
      TimeZone: profile.TimeZone,
      Locale: profile.Locale,
      Organization: profile.Organization,
      Title: profile.Title,
      Division: profile.Division,
      Department: profile.Department,
      EmployeeNumber: profile.EmployeeNumber,
      RoleCodes: profile.AssignedRoles || [],
    });
    setProfileOpen(true);
  };

  const saveProfile = async (updatedProfileDraft) => {
    const submittedDraft = {
      ...(profileDraft || {}),
      ...(updatedProfileDraft || {}),
    };
    const isNewProfile = !submittedDraft.ProfileId;
    const requestedRoleCodes = Array.isArray(submittedDraft.RoleCodes)
      ? submittedDraft.RoleCodes
      : String(submittedDraft.RoleCodes || '')
        .split(',')
        .map((roleCode) => roleCode.trim())
        .filter(Boolean);
    const normalized = {
      ...submittedDraft,
      Address: normalizeClientProfileAddress(
        submittedDraft.Address,
        { defaultCountry: isNewProfile },
      ),
      Locale: submittedDraft.Locale
        || (isNewProfile ? DEFAULT_CLIENT_PROFILE_LOCALE : ''),
    };
    delete normalized.RoleCodes;

    setBusy(true);
    setError('');
    setToastMessage('');
    try {
      let updated = await runOperation(
        workspaceRef.current,
        CLIENT_SECURITY_OPERATIONS.saveProfile,
        request({ ProfileForm: normalized }),
        'The profile could not be saved.',
      );
      setWorkspace(updated);
      if (canAssignRoles) {
        updated = await runOperation(
          workspaceRef.current,
          CLIENT_SECURITY_OPERATIONS.assignRoles,
          createSecurityRequest({
            workspace: updated,
            clientId,
            appId,
            currentEnvironment,
            overrides: {
              SelectedProfileId: updated.SelectedProfileId,
              RequestedRoleCodes: requestedRoleCodes,
            },
          }),
          'The profile roles could not be saved.',
        );
        setWorkspace(updated);
      }
      showSuccessToast(
        updated?.StatusMessage
          || (normalized.ProfileId
            ? 'Local profile saved.'
            : 'Local profile created.'),
      );
      setProfileOpen(false);
      return updated;
    } catch (ex) {
      showActionError(
        'Profile could not be saved',
        ex?.message || 'The profile could not be saved.',
      );
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openRepair = (profile) => {
    openUserSearch({
      purpose: 'repair',
      targetProfile: profile,
    });
  };

  const repairMapping = async () => {
    return execute(
      CLIENT_SECURITY_OPERATIONS.repairEntraMapping,
      request({
        SelectedProfileId: repairDraft.profile.Id,
        ProfileForm: repairDraft.form,
      }),
      'Local Entra mapping repaired.',
      () => setRepairDraft(null),
      'Entra mapping could not be repaired',
      'The Entra mapping could not be repaired.',
    );
  };

  const closeAccessAction = () => {
    setAccessAction(null);
  };

  const changeAccess = async () => {
    return execute(
      CLIENT_SECURITY_OPERATIONS.setUserAccess,
      request({
        SelectedProfileId: accessAction.profile.Id,
        AccessAction: accessAction.action,
        ConfirmAccessImpact: true,
      }),
      `Local Convoke access ${accessAction.action} completed.`,
      closeAccessAction,
      'Convoke access could not be changed',
      'Convoke access could not be changed.',
    );
  };

  return (
    <div className="resource-workspace-overview command-entry command-security command-security--user-profiles">
      <SecurityToolbar
        busy={loading || busy}
        onRefresh={refresh}
        ariaLabel="User Profiles actions"
        actions={[
          ...(canSaveProfile ? [{
            key: 'add-client-user-profile',
            label: 'Add Profile',
            iconKey: 'add',
            disabled: busy || !profileMetadata,
            onClick: openNewProfile,
          }] : []),
          ...(canLoadGuestAccess ? [{
            key: 'manage-guest-invitations',
            label: 'Guest Invitations',
            iconKey: 'email2',
            disabled: loading || busy,
            onClick: () => setGuestInvitationsOpen(true),
          }] : []),
        ]}
      />
      <SecurityState
        loading={loading}
        error={error}
        label="Client User Profiles"
      />
      {toastMessage ? (
        <ToastNotifications position="top" message={toastMessage} />
      ) : null}
      {!loading && workspace ? (
        <section className="resource-workspace-panel command-security__user-profiles-panel">
            <div className="resource-workspace-section-title">
              <div>
                <h3>User Profiles</h3>
                <p>
                  View and manage profiles for this client application.
                </p>
              </div>
            </div>
            <TableControl
              data={workspace.Profiles || []}
              defaultNumRows={50}
              disableTableTitle
              headerFeatures={[
                'search',
                'filter',
                'tableStyles',
                'columnSettings',
              ]}
              enableMultiSelect={false}
              loadingState={busy}
              metadata={profileGridMetadata}
              mgr={workspaceManager}
              onEdit={canSaveProfile ? openProfile : undefined}
              customQuickViewList={() => [
                ...(canSaveProfile ? [{
                  name: 'Edit Profile',
                  customFunction: openProfile,
                }] : []),
                ...(canSetUserAccess ? [{
                  name: 'Activate / Deactivate Convoke Access',
                  customFunction: (profile) => {
                    setAccessAction({
                      profile,
                      action: profile.ConvokeProfileStatus === 'Active'
                        ? 'deactivate'
                        : 'activate',
                    });
                  },
                }] : []),
                ...(canSearchEntraUsers && canRepairEntraMapping ? [{
                  name: 'Repair Entra Mapping',
                  customFunction: openRepair,
                }] : []),
                ...(canSetUserAccess ? [{
                  name: 'Remove Convoke Access',
                  customFunction: (profile) => {
                    setAccessAction({
                      profile,
                      action: 'remove',
                    });
                  },
                }] : []),
              ]}
            />
        </section>
      ) : null}

      <ClientGuestInvitationsPanel
        open={guestInvitationsOpen}
        clientId={clientId}
        appId={appId}
        currentEnvironment={currentEnvironment}
        canLoad={canLoadGuestAccess}
        canInvite={canInviteGuest}
        canReconcile={canReconcileGuestAccess}
        canRevoke={canRevokeGuest}
        onClose={() => setGuestInvitationsOpen(false)}
        onSuccess={showSuccessToast}
        onActionError={showActionError}
      />

      <DialogControl
        openDialog={searchOpen}
        title="Select User Mapping"
        subTitle="Choose a provider with directory search to repair this profile's external identity mapping."
        okText="Close"
        cancelText="Cancel"
        onCancel={() => setSearchOpen(false)}
        onX={() => setSearchOpen(false)}
        onOk={() => setSearchOpen(false)}
        disableOk={busy}
        loadingOk={busy}
      >
        <div className="command-security__add-user-dialog">
          <ConvokeDialogMessage
            message={providerLoadError}
            tone="error"
          />
          <FieldControl
            fieldMetadata={activeProviderField}
            value={selectedProviderMappingId}
            onChange={(value) => {
              const providerMappingId = String(value || '');
              setSelectedProviderMappingId(providerMappingId);
              setSearchAttempted(false);
              setWorkspace((current) => current
                ? { ...current, EntraSearchResults: [] }
                : current);
              if (providerMappingId) {
                loadDirectoryUsers(providerMappingId);
              }
            }}
            disabled={busy || activeProviderMappings.length === 0}
            disableError
          />
          {searchAttempted ? (
            <CardGridControl
              className="command-security__directory-user-card-grid"
              rows={workspace?.EntraSearchResults || []}
              metadata={directoryUserCardMetadata}
              cardFields={directoryUserCardMetadata[0].Fields}
              rowKey={(user) => user.ObjectId}
              selectable
              selectedRowKeys={[]}
              onSelectedRowKeysChange={(_keys, user) => selectEntraUser(user)}
              readOnly={false}
              disabled={busy}
              searchable
              disableInlineEdit
              disableCardClickEdit
              activeFilterEnabled={false}
              emptyContent="No users matched this search."
              minCardWidth="12rem"
              maxColumns={2}
            />
          ) : null}
        </div>
      </DialogControl>
      <ClientProfileFormPanel
        open={profileOpen}
        title={profileDraft?.ProfileId ? 'Edit Profile' : 'Add Profile'}
        metadata={profileMetadata}
        formFields={profileFields}
        itemData={profileDraft}
        onChange={(_value, fieldName, nextItem) => {
          setProfileDraft((currentDraft) => mergeClientProfileDraftChange(
            currentDraft,
            fieldName,
            nextItem,
          ));
        }}
        onSave={saveProfile}
        onCancel={() => {
          setProfileOpen(false);
        }}
        setItemData={setProfileDraft}
        canWrite={canSaveProfile}
        busy={busy}
      />
      <SidePanelFormControl
        open={!!repairDraft}
        title="Repair Entra Mapping"
        metadata={{
          Name: 'ClientEntraMappingRepair',
          DisplayName: 'Repair Entra Mapping',
          Fields: [
            {
              FieldName: 'EntraObjectId',
              DisplayName: 'Selected Entra Object ID',
              FieldType: 'Label',
            },
            {
              FieldName: 'UserPrincipalName',
              DisplayName: 'Selected User Principal Name',
              FieldType: 'Label',
            },
          ],
        }}
        formFields={[
          {
            FieldName: 'EntraObjectId',
            DisplayName: 'Selected Entra Object ID',
            FieldType: 'Label',
          },
          {
            FieldName: 'UserPrincipalName',
            DisplayName: 'Selected User Principal Name',
            FieldType: 'Label',
          },
        ]}
        itemData={repairDraft?.form}
        onSave={repairMapping}
        onCancel={() => setRepairDraft(null)}
        onUpdateSidePanel={() => {}}
        saveButtonText="Repair Mapping"
        setOpenEditSidePanel={(open) => {
          if (!open) setRepairDraft(null);
        }}
        setItemData={(form) => setRepairDraft((current) => ({
          ...current,
          form,
        }))}
        viewOnly={!canRepairEntraMapping}
      />
      <DialogControl
        openDialog={!!accessAction}
        title={`${accessAction?.action || 'Change'} Convoke Access`}
        subTitle="Only local Convoke access changes. The Entra user remains unchanged."
        okText="Confirm"
        cancelText="Cancel"
        onCancel={closeAccessAction}
        onX={closeAccessAction}
        onOk={changeAccess}
        disableOk={busy}
        loadingOk={busy}
      >
        <p>
          {accessAction?.action}
          {' '}
          local Convoke access for {accessAction?.profile?.Name}? Last-client-
          administrator safeguards are rechecked on the server.
        </p>
      </DialogControl>
      <DialogControl
        openDialog={!!actionError}
        title={actionError?.title || 'Action failed'}
        okText="Close"
        cancelText=""
        onCancel={() => setActionError(null)}
        onX={() => setActionError(null)}
        onOk={() => setActionError(null)}
      >
        <ConvokeDialogMessage message={actionError?.message} tone="error" />
      </DialogControl>
    </div>
  );
};
